import type { BehaviorSlug } from "./behaviors";

/**
 * A hand-authored watch party discussion question. Not generated: these are
 * written course by course, so most courses have none yet.
 */
export interface DiscussionQuestion {
  /** Read aloud to the room. */
  prompt: string;
  /** What a good answer sounds like, for the facilitator only. */
  facilitatorNote: string;
  /** Asked if the room goes quiet. */
  probe: string;
  /** Suggested discussion time in minutes. */
  minutes: number;
  behaviors: BehaviorSlug[];
}

export interface CourseSession {
  title: string;
  /** Training Pipeline numeric course ID. */
  courseId?: number;
  body: string;
  format?: string; // e.g. "Virtual Session (30 min) + Journal (15 min) + Activity (15 min)"
  /** Watch party deck for this session. */
  discussionQuestions?: DiscussionQuestion[];
}

export interface Course {
  slug: string;
  title: string;
  /** Training Pipeline numeric course ID (from courses_rows.json). */
  courseId?: number;
  featured?: boolean;
  image?: string;
  // Direct MP4 URL. When present, `video` takes precedence over `image` in the
  // hero media slot (and `image` is used as the poster frame). When neither
  // exists, the hero renders a brand gradient placeholder.
  video?: string;
  // Populated courses have everything below. Stub courses have only the fields above.
  detailed?: boolean;

  tagline?: string; // one sentence, max 20 words
  level?: string; // "Foundational" | "Intermediate" | "Advanced" | "All Levels"
  weeklyTime?: string; // "30 minutes/week"
  duration?: string; // "3 weeks"
  whoFor?: string; // max 40 words
  overview?: string; // max 60 words
  outcomes?: string[]; // 3-4 items, max 15 words each
  behaviors?: string[]; // Leader Behavior names
  sessions?: CourseSession[];
  /** Watch party fallback deck, used when a course has no per-session decks. */
  discussionQuestions?: DiscussionQuestion[];
}

/** Internal course detail path. Course cards link here, not off-site. */
export function courseUrl(slug: string) {
  return `/course-catalog/${slug}/`;
}

export const CATALOG_PDF_FILENAME = "LEL-Course-Catalog.pdf";
export const CATALOG_PDF_URL = `/${CATALOG_PDF_FILENAME}`;

const FEATURED_SLUGS = new Set([
  "balancing-priorities",
  "the-art-of-influence",
  "new-leader-boot-camp",
  "adapting-your-leadership-style",
  "delegating-for-development",
  "conquering-conflict",
  "creating-a-culture-of-trust",
  "psychological-safety",
  "leadership-essentials-with-a-foundation-of-emotional-intelligence",
  "the-experienced-leader-boot-camp",
  "collaborative-coaching-conversations",
  "driving-results-through-goal-setting",
  "emotional-and-cognitive-empathy",
  "making-better-decisions-through-critical-thinking",
  "manage-your-time-master-your-life",
  "navigating-organizational-politics",
  "on-the-spot-development-conversations",
  "weathering-the-storm-learning-to-be-resilient",
]);

// Detail overlays, merged onto the base course list by slug.
const details: Record<string, Partial<Course>> = {
  "leadership-essentials-with-a-foundation-of-emotional-intelligence": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Leadership-Essentials-with-a-Foundation-of-Emotional-Intelligence-1-scaled.jpg",
    whoFor:
      "This course is for employees and managers at any level who want to resolve conflict constructively, create a productive work environment, and build and mend relationships.",
    overview:
      "Motivated business professionals are looking for every edge to be successful in the workplace. Naturally, many develop expertise in technical or task-oriented skills in order to enhance their careers. However, those who have a high level of awareness and control over their emotions, who use their emotions to connect with others and develop positive relationships, will find even greater success in the workplace. This skill is known as emotional intelligence, and it is an essential element in the business world, especially in high-stress environments. Not controlling your emotions can preoccupy your thoughts and interfere with your ability to evaluate a situation objectively. This course provides the knowledge and tools needed to develop emotional intelligence, enabling leaders to confront difficult issues and manage change with clarity and composure.",
    outcomes: [
      "Define emotional intelligence and identify its five components.",
      "Recognize how intellectual intelligence and emotional intelligence are connected and identify the key skills needed to build emotional intelligence.",
      "Explore methods to develop more positive relationships at work by utilizing two key interpersonal skills.",
      "Identify social skills you can evolve into effective communication with others.",
    ],
    behaviors: [
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Inspires",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Introduction to Emotional Intelligence",
        body: "This module provides participants with an overview of emotional intelligence and why it is important in your profession. We will also cover the five areas of EQ (Emotional Quotient/Intelligence).",
      },
      {
        title: "Mind: Self-Awareness and Self-Regulation",
        body: "This module will explore how emotion influences your thoughts and actions. This incorporates using self-awareness and self-regulation strategies as key skills to build emotional intelligence.",
      },
      {
        title: "Heart: Motivation and Empathy",
        body: "An important goal in developing emotional intelligence is to develop more positive relationships with others at work. In this module, we will explore methods to do this by using two key interpersonal skills: motivation and empathy.",
      },
      {
        title: "Mouth: Social Skills",
        body: "This module illustrates the social skills needed to build your emotional intelligence. Participants will learn how to communicate their feelings and needs clearly to another person while remaining respectful of their feelings and needs.",
      },
    ],
  },
  "the-invincible-influencer": {
    detailed: true,
    level: "Advanced",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/02/The-Invincible-Influencer-1-scaled.jpg",
    whoFor:
      "This course is designed for anyone that would benefit from increased levels of influence within their organization. The strategies taught are especially valuable for line managers and HR leaders, but all leaders can benefit from this course.",
    overview:
      "The Invincible Influencer stands undefeated. Using superpowers such as reliability, commonality, humility, and a relentless drive for organizational results, the Invincible Influencer is able to leap organizational skill gaps in a single bound. By the end of this fast-paced course, you will experience strategies that drive influence and find immediate application back within your organization. Let’s level-up your ability to influence!",
    outcomes: [
      "Identify Indigo’s influencing superpowers and explain how they drive effective influence",
      "Analyze how personality styles impact communication and influence effectiveness",
      "Apply a structured change management approach to influence outcomes in real situations",
      "Leverage social capital to influence key stakeholders and accelerate impact",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Inspires",
    ],
    sessions: [
      {
        title: "Meet Indigo and His Influencing Superpowers",
        body: "In this session, we will introduce Indigo the Influencer and explore his multiple superpowers that he uses to influence others. Throughout this session, you will explore how to utilize these same superpowers to become a more versatile influencer.",
      },
      {
        title: "Communication as a Key to Influence",
        body: "We need to understand natural reactions to change in order to influence others effectively. During this session, we will explore a foundational change management approach that we can implement during our influence efforts.",
      },
      {
        title: "Influencing Change",
        body: "There are four major personality styles that impact interaction among employees within the workplace. During this session, we will explore how personality style impacts our ability to influence others.",
      },
      {
        title: "Leveraging Social Capital",
        body: "You do not need to influence everyone in the organization! You need to influence a select group of people and leverage their social capital. By understand the social leaders in your organization, you can move faster and have more impact.",
      },
    ],
  },
  "new-leader-boot-camp": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/New-Leader-Boot-Camp-1-scaled.jpg",
    whoFor:
      "This course is appropriate for anyone who is new to a leadership role, currently serving in a leadership role, or is considering a role in leadership.",
    overview:
      "Becoming a new leader can be overwhelming. Navigating the murky waters of your new role can be darn-right scary! The New Leader Boot Camp can help ease the anxiety of settling into your new role. This course provides you with the knowledge, skills, and tools necessary to develop a solid 30-60-90-day action plan that will set you on the path to leadership success. You will also learn about the essential core leadership competencies and attributes grounded in the philosophy and practice of Servant Leadership.",
    outcomes: [
      "Define leadership and the role of the leader.",
      "Gain an understanding of the philosophy and practices of Servant Leadership.",
      "Recognize the importance of communication as a new leader.",
      "Set the expectations to seamlessly transition from peer to boss.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Inspires",
      "Matches Style to Needs",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Sets Goals",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Leadership 101",
        body: "In this module, we define leadership, the role of the leader, leadership styles, and leader behavior competencies. This is the first step in truly understanding and establishing your position as a leader.",
      },
      {
        title: "Servant Leadership",
        body: "In this module, you will learn about the values and principles of Servant Leadership and how to apply them in your leadership practice.",
      },
      {
        title: "Communication",
        body: "In this module, participants explore their style of communication and the styles of others. Participants are provided with tools and techniques to adapt their communication styles to others. Additionally, we demonstrate how these tools and techniques are applied when conducting the Four Core Leadership Conversations.",
      },
      {
        title: "Transitioning from Peer to Leader",
        body: "Making the transition from peer to leader can be tricky, and one of the hardest things any of us ever has to do in our careers. You must deal with the fact that in the eyes of employees, you are no longer “one of us.” In this module, participants learn how to seamlessly and systematically transition from peer to leader. We provide participants with specific actions they can take to reduce any fears and frustration they may have and set them on the path to be the kind of leader others want to follow.",
      },
    ],
  },
  "the-experienced-leader-boot-camp": {
    detailed: true,
    level: "Intermediate",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Todays-Leadership-Challenges-–-The-Experienced-Leader-Boot-Camp.jpg",
    whoFor:
      "This course would benefit experienced leaders who seek to better understand the unique challenges managers face in today’s business world, while developing the core skills needed to overcome those challenges and lead effective teams.",
    overview:
      "Experienced leaders must know how to adapt to the rapidly changing business environment with flexibility and skill to mitigate threats and seize opportunities as they arise. They must know how to lead their teams to work together effectively through a shared vision. In this course, we will explore the five characteristics of an experienced leader and learn to recognize the importance of vision and passion in leadership. Additionally, participants will understand how to critically assess opportunities using Second Level Thinking to make long-term decisions, and they will learn how to better understand and respond to team members’ behaviors and motives.",
    outcomes: [
      "Understand and practice the five core components of an experienced leader.",
      "Create a vision and share it with passion and purpose.",
      "Explore and utilize Second Level Thinking.",
      "Manage team member challenges with sensitivity and fairness.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Innovates",
      "Inspires",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Challenges of Becoming an Experienced Leader - Vision",
        body: "In this module, participants will learn the five core components to becoming an experienced leader: Vision, Passion, Second Level Thinking, Managing Others, and Relationships. We will explore how good leaders create and share a vision of the future that resonates with others and we will take part in an activity that will allow us to dive further into that vision.",
      },
      {
        title: "Challenges of Becoming an Experienced Leader - Passion and Second Level Thinking",
        body: "Experienced leaders use passion to attract others to their vision, to generate enthusiasm and participation, and to inspire others to believe that they are capable of accomplishing great things. Similarly, Second Level Thinking enables leaders to make better decisions that give them a much better chance of a positive outcome in the long term. This module will establish these concepts in detail.",
      },
      {
        title: "Challenges of Becoming an Experienced Leader - Managing Others and Relationships",
        body: "Leaders must have a comprehensive understanding of their organization’s unique challenges and know how to address problems with particular employees. We will explore how to properly analyze organizational and employee-related challenges in this module. The module will also give leaders valuable tools for effective interaction with employees including problem solving, goal creation, conflict mediation, and methods for managing relationships.",
      },
    ],
  },
  "performance-management": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Performance-Management-.jpg",
    whoFor:
      "This course is designed for executives and managers who need to learn Performance Management and want to learn how to improve the effectiveness of their team members.",
    overview:
      "Annual performance appraisals have the potential to be a dynamic and powerful method for managing employee performance. However, the results are typically less than ideal. Instead of a single performance appraisal event, leaders should use a strategy for ongoing Performance Management. Performance Management involves establishing a systematic process that managers can use to support and develop employees. This process focuses on creating clear expectations for individual performance based on the organization’s goals. The goal of this course is to help managers improve their people-development skills by implementing a performance management system that helps every employee achieve their peak performance potential.",
    outcomes: [
      "Differentiate between performance management and annual performance appraisals.",
      "Utilize the four-step approach for productive performance conversations and avoid conversation minefields.",
      "Create a performance management plan.",
    ],
    behaviors: [
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Pursues Self-Development",
    ],
    sessions: [
      {
        title: "Introduction to Performance Management",
        body: "In this session, we will define Performance Management and learn why it matters. We will differentiate between performance development and annual performance appraisals. We will also identify the benefits of using a systematic approach to performance management.",
      },
      {
        title: "Performance Conversations",
        body: "This session explores the five-step approach to having effective performance conversations. We will identify the most common minefields that may diminish the success of the conversation.",
      },
      {
        title: "Measuring Performance",
        body: "This session focuses on how to tie organizational goals to competencies and accomplishments. We will explore the most useful performance measures for individual elements and how to implement a process to assess current performance of individual employees.",
      },
    ],
  },
  "strengthening-teams-through-communication": {
    detailed: true,
    level: "All Levels",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Strengthening-Teams-through-Communication.jpg",
    whoFor: "This course is for anyone who leads a team or is a member of a team.",
    overview:
      "It is not unusual to hear people in an organization say, “We have a communication problem.” This course is designed to provide participants with information and techniques to analyze and enhance their communication skills and help others communicate more effectively. Communication takes many forms: words, tone, eyes, and body language, to name a few. This course helps participants understand the hidden meanings behind the various types of communication and devise strategies to communicate clearly and avoid unnecessary conflict.",
    outcomes: [
      "Analyze the communication process, identify breakdown points, and improve message clarity and effectiveness",
      "Evaluate personality style differences, recognize communication preferences, and adapt communication to improve understanding",
      "Identify common communication pitfalls, assess their impact, and apply strategies to improve communication effectiveness",
      "Demonstrate active listening techniques to improve understanding, build trust, and strengthen team interactions",
      "Apply structured communication strategies to manage difficult conversations and respond effectively in high-emotion situations",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Inspires",
      "Leverages Diversity",
      "Matches Style to Needs",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "The Communication Process",
        body: "Changing technology makes focus on communication in the workplace essential. Teamwork and communication go hand in hand. For a team to be successful, each member must communicate effectively to ensure the group as a whole is always up to date on the task at hand. Whether you’re working with a small project team or a business’ entire staff, it’s essential to focus on communicating effectively to realize the benefits of team communication. This module contains an overview of the communication process and the common pitfalls in the process. We also define communication filters and demonstrate how filters impact the way we communicate with others.",
      },
      {
        title: "Personality Styles as a Primary Communication Filter",
        body: "Personality styles are the way we communicate with others and how others communicate with us. This module contains an overview of the degrees of assertiveness and responsiveness in communication, how to determine your personality style and identify the personality style of others, and how to adapt your communication style to the communication styles of others.",
      },
      {
        title: "Avoiding Communication Pitfalls",
        body: "With communication comes miscommunication. Miscommunications have serious consequences. They can tarnish your reputation, upset clients, team members, or even lead to lost revenue. Effective team communication can transform your organization in so many ways! And yet, many employees are not happy with the way their teams communicate. In this module, we address the critical mistakes we make when communicating and best practices for avoiding these mistakes.",
      },
      {
        title: "Listening Skills",
        body: "Listening may seem simple. However, more and more team leaders and companies are understanding there are skills necessary to listening well, and are reaping the rewards. The direct act of listening enriches workplace relationships, improves team productivity, and enhances your mood. While conflict from communication is inevitable, active listening changes misunderstandings into positive action. In this module, we learn approaches, tools, and techniques to enhance your active listening skills and minimize conflict.",
      },
      {
        title: "Challenging Conversations",
        body: "From delivering performance reviews to saying no to new projects or extra work, difficult conversations are part of life. The ability to tackle tough issues with tact and respect is an essential skill for any professional. By cultivating a culture of trust and openness and fostering healthy communication practices, you can successfully navigate difficult topics and help your team thrive. In this module, we provide you with proven approaches to address communication challenges. Topics include the power of “I” statements, methods to de-escalate emotions, manage extreme anger, manage hidden emotions, giving and receiving negative feedback.",
      },
    ],
  },
  "leading-change": {
    detailed: true,
    level: "Intermediate",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Leading-Change.jpg",
    whoFor:
      "Although this course applies to all leaders and followers, the content is most applicable to those with experience in a leadership role. This course is also valuable for those with leadership aspirations and for those who are anticipating transitioning into a leadership role within 6-12 months.",
    overview:
      "It’s important to understand how leading change can generate a positive or negative outcome at an organizational level. In this course, participants will define the eight common pitfalls of leading change and learn the skills required to be effective. Participants will gain insight into the three Cs of leading change and recognize three potential hurdles to overcome. This course will also examine an eight-step process and change models including Lewin’s model and the ADKAR model.",
    outcomes: [
      "Define leading change opportunities and their impact.",
      "Identify skills required to effectively lead change while avoiding common pitfalls.",
      "Recognize leading change models to accelerate successful implementation.",
      "Determine successful leading change dynamics and hurdles to overcome.",
    ],
    behaviors: [
      "Champions Change",
      "Communicates",
      "Focuses on Results through People",
      "Inspires",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "The Big Idea and Business Case for Leading Change",
        body: "This module will explore the evolution of leading change. Participants will examine the positive and negative components of leading change and the subsequent organizational impact.",
      },
      {
        title: "How Leading Change Applies to You",
        body: "This module will explore the eight common pitfalls associated with leading change. Participants will also review the five skills required for effectively leading change.",
      },
      {
        title: "Leading Change Models",
        body: "This module explores the first four steps of the “Big Opportunity” change model. Participants will also summarize the ADKAR model for change in addition to Lewin’s change management model.",
      },
      {
        title: "Leading Change - Application and Best Practices",
        body: "This module will review steps five through eight of the “Big Opportunity” change model. Participants will also review the three Cs of leading change successfully as well as the three hurdles to overcome.",
      },
    ],
  },
  "psychological-safety": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Psychological-Safety.jpg",
    whoFor:
      "This course is designed to provide team leaders, business professionals, and team members with an insight into psychological safety that has been proven as a vital source for developing high performing teams.",
    overview:
      "Psychological safety is the belief that one will not be punished or humiliated for speaking up with ideas, questions, concerns or mistakes. Studies show that when your team is high in psychological safety, they are more engaged, more creative and flourish in their organization. This course will explore the behaviors and skills needed to promote psychological safety in your workplace and develop a culture of knowledge and innovation.",
    outcomes: [
      "Define the concept of psychological safety and its importance.",
      "Recognize the behaviors that destroy psychological safety and ways to avoid them in the workplace.",
      "Identify the behaviors that enhance and promote psychological safety.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Defining Psychological Safety",
        body: "In this module, we will define psychological safety and how it can impact an organization overall. Participants will explore what a psychologically safe team should look like and reasons why psychological safety does not exist in a situation.",
      },
      {
        title: "Behaviors that Destroy Psychological Safety",
        body: "A workplace with a lack of psychological safety does not allow people to take risks in thoughts, ideas and problem solving. This module will explore the behaviors that destroy psychological safety in our workplace. We will examine how improper cultures in organizations affect psychological safety.",
      },
      {
        title: "Behaviors that Build Psychological Safety",
        body: "A psychologically safe team exhibits a higher level of engagement and increased motivation to tackle challenging issues. Psychological safety creates more learning and development opportunities, and better team performance. This module focuses on the behaviors that build psychological safety. We will learn the skills that make our team feel more psychologically safe.",
      },
    ],
  },
  "the-mentoring-mindset": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/07/The_Mentoring_Mindset.jpg",
    whoFor:
      "This course is suitable for leaders who want to become skilled mentors. Leaders, managers, and employees will learn to improve their self-awareness as they recognize and utilize the skills necessary to effectively mentor others through a better understanding of the mentoring mindset. Anyone who will be a mentee in their professional life will also benefit from the course.",
    overview:
      "Everyone from the newest employee to the CEO can benefit from having a mentor. This course offers a collection of skills and strategies to help build a mentoring mindset as a mentor or mentee for the purpose of personal and professional development. Participants will learn to create a positive mentoring relationship and understand the expectations of their mentor or mentee.",
    outcomes: [
      "Understand the mentoring relationship, including the role of the mentor and mentee.",
      "Discover effective questioning techniques as a mentor.",
      "Deliver constructive criticism to help a mentee improve their abilities.",
      "Successfully move beyond the mentoring relationship.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Communicates",
      "Demonstrates Self-Management",
      "Practices Self-Awareness",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Introduction to the Mentoring Relationship",
        body: "In this module, we will promote an understanding of the mentoring relationship and how it is different from a boss – employee relationship. We will distinguish the responsibilities of both the mentor and the mentee and discuss the value of the mentoring mindset.",
      },
      {
        title: "Responsibilities of the Mentee",
        body: "In this module, we will recognize our responsibilities as a mentee in the mentoring relationship. We will learn how to receive feedback openly and discover ways to protect and contribute to the relationship. We will explore how to bring the mentoring relationship to a close at the proper time.",
      },
      {
        title: "How to be a Good Mentor",
        body: "An effective mentor provides guidance through coaching techniques, rather than direct instruction. In this module, participants will learn how to implement mentoring strategies such as coaching and constructive criticism to help develop their mentees.",
      },
    ],
  },
  "leading-remote-teams": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Leading-Remote-Teams.jpg",
    whoFor:
      "This program is appropriate for anyone who manages team members who work offsite, ranging from a single remote worker to an entire team. While the course is geared toward supervisors and managers, the content is relevant to anyone who is responsible for the work of or collaborates closely with an offsite team member.",
    overview:
      "The workforce is rapidly changing. An increasing number of employees work offsite by choice or by necessity. Given the increasingly global nature of today’s organizations—as well as improvements in technology that facilitate virtual collaboration and a workforce that insists on more flexibility and independence—this trend is likely to keep gaining momentum. Whether your employees work at home near the onsite team or are scattered around the country, by improving your ability to manage offsite employees, you will empower yourself in many ways. You will increase your ability to recruit, hire, and retain the right employees for a job. You will help ensure that your team is cohesive and productive. You will know how to avoid costly, time-consuming problems and help your team meet its goals.",
    outcomes: [
      "Identify the benefits and challenges of offsite working relationships as well as the factors and tools that must be in place for an offsite employee to be effective.",
      "Define the role that trust and communication play in the success of an offsite relationship.",
      "Employ strategies that help offsite employees stay on track and that deal with common problems faced by managers.",
      "Utilize a set of best practices for successfully managing offsite employees.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Communicates",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Innovates",
      "Matches Style to Needs",
      "Practices Self-Awareness",
      "Sets Goals",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Introduction to Leading Remote Teams",
        body: "We begin our discussion by examining ‘the right stuff’—that is, the people, situation, and tools needed for an offsite working relationship to flourish. While working remotely may be appealing to many employees, not everyone is cut out for it.",
      },
      {
        title: "The Essentials",
        body: "No two factors are more important to a successful remote working relationship than trust and communication. In this module, we will explore both. Managers need to be able to trust their offsite employees to do their jobs without direct supervision. Knowing that they are trusted helps motivate employees and gives them the confidence and drive they need to work on their own. In addition to trust—and inseparable from it—clear, ongoing communication is essential to an offsite working relationship. Without that communication, the relationship will have little chance of succeeding.",
      },
      {
        title: "Concept Model and Process",
        body: "All employees need preparation to do their jobs. It’s no different for employees who will be working remotely, whether they are new to the organization or making the transition from onsite to offsite. Managers do not sit their new employees down at a desk and tell them to get to work. To be successful at the job, the new hire needs information about the organization, introductions to colleagues, details about the job, and more. It’s the same with an offsite employee. It would be unreasonable to expect either a new or current employee to jump into offsite work without a certain amount of preparation. This module will dive into how to implement this process effectively.",
      },
      {
        title: "Application and Best Practices",
        body: "We cover a lot of information in the course. In this final module, we pull it all together. In addition, we offer several best practices you can apply on the job immediately to maximize your team members’ chances of meeting their goals and being productive members of the team.",
      },
    ],
  },
  "true-empowerment": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/True-Empowerment-1-1-scaled.jpg",
    whoFor:
      "This course is ideal for any leader who wants their employees to be more productive, embrace change and be committed. This course will provide participants with the necessary insights to empower their employees.",
    overview:
      "Employees want to feel respected and empowered. True empowerment is comprised of knowledge and authority. It is developed through skill and knowledge building, as well as confidence and trust. In this course, we will define empowerment and understand its impact on the organization and its employees through the True Workforce Empowerment Model. We will then explore the Empowerment Relationship Map, and how that is developed through the delegation cycle.",
    outcomes: [
      "Define empowerment and its impact on the way we work.",
      "Recognize the True Workforce Empowerment Model and understand the four types of empowerment.",
      "Recognize the Empowerment Relationship Map and associated directive and supportive behaviors.",
      "Learn to assign specific tasks to build an employee’s ability to be empowered through knowledge and authority in a variety of areas.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Introduction to Empowerment",
        body: "In this module, we will define empowerment and its importance in employee engagement. We will explore the True Workforce Empowerment Model and the four types of empowerment.",
      },
      {
        title: "Empowerment Relationship Map",
        body: "In this module, we will define and explore the Empowerment Relationship Map, discuss the Directive and Supportive Leader Behaviors, and learn how to apply them in the process of developing others toward empowerment.",
      },
      {
        title: "Empowerment through Development",
        body: "In this module, participants will examine the Delegation to Development Cycle and how helping people move through the cycle creates the opportunity to develop knowledge, authority, and true empowerment.",
      },
    ],
  },
  "managing-organizational-conflict": {
    detailed: true,
    level: "Intermediate",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Managing-Organizational-Conflict-1-scaled.jpg",
    whoFor:
      "This course is designed for leaders and managers who want to form a low-conflict, high-collaboration environment within their organization. This course will help establish conflict resolution guidelines and practices for their team members.",
    overview:
      "Have you ever found yourself dreading having to work with a colleague who is particularly stubborn or bossy? Or perhaps replaying an encounter with your supervisor that left you feeling frustrated? Most people have been involved in some form of conflict in the workplace, ranging from mild disagreements to explosive standoffs. Conflict can be an obstacle to organizational success and personal job satisfaction. When two or more people work together, conflict is almost inevitable. Although you may not eliminate conflict entirely, you can take control of it. In this course, you will learn how to successfully handle conflict and prevent unnecessary conflict from occurring in the first place through the implementation of effective resolution strategies. By the end of this course, you will be able to create a positive working environment in which conflict is dealt with collaboratively.",
    outcomes: [
      "Identify your preferred strategy for handling conflict.",
      "Recognize the characteristics and drawbacks of each typical approach to conflict.",
      "Identify various sources of conflict and implement strategies for effectively resolving conflicts.",
      "Apply strategies that address issues before they escalate into problems.",
    ],
    behaviors: ["Communicates", "Diagnoses Needs", "Solves Problems", "Builds Trust"],
    sessions: [
      {
        title: "Your Approach to Conflict",
        body: "In this module, we will identify where conflict originates, discover your preferred strategy for handling conflict, explore the chain of dialogue, and examine the characteristics and drawbacks of each typical approach to conflict.",
      },
      {
        title: "Managing Conflicts",
        body: "In this module, we will identify the five characteristic styles of the Blake and Mouton Model used by managerial positions to deal with conflict in the workplace. We will explore each of them as strategies that will effectively manage conflict.",
      },
      {
        title: "Sources of Conflict",
        body: "This module will focus on learning how to deal with conflict as it comes up and recognizing the various sources of conflict. We will identify and analyze the strategies for resolving conflicts stemming from each source.",
      },
      {
        title: "Preventing Conflict",
        body: "Every leader in an organization needs to be able to prevent and manage conflict. This module explores the behaviors that minimize tension and conflict. We will explore strategies to proactively address issues before they escalate.",
      },
    ],
  },
  "4p-series-communication-from-the-passion-perspective": {
    detailed: true,
    level: "Foundational",
    duration: "2 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/4-P-Series-Passion.webp",
    whoFor:
      "This course is designed for anyone who wants to understand the dynamics of effective communication and improve how well their messages are received. All leaders can benefit from this course.",
    overview:
      "Social scientists categorize people into four major personality styles: Power, Passion, People, and Precise. During this course, you will examine how to ideally communicate with someone with the Passion perspective. Through adjustments in your approach, you can reduce tension and increase effectiveness when working with this personality.",
    outcomes: [
      "Recognize Passion style's needs and adjust your style based on their needs.",
      "Identify methods to manage tension or conflict effectively with someone with a Passion personality preference.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Communication Strategies for the Passion Personality",
        body: "In this session, we will explore how to ideally connect with and relate to someone with a Passion personality. We will focus on methods of communication, factors that motivate, and perceptions of the Passion style.",
      },
      {
        title: "Managing Tension with the Passion Personality",
        body: "In this session, we will examine how the Passion personality reacts to tension or conflict as their back-up behavior. We will discuss how the Passion personality escalates through their Z conflict pattern as well as methods for de-escalating tension.",
      },
    ],
  },
  "4p-series-communication-from-the-people-perspective": {
    detailed: true,
    level: "Foundational",
    duration: "2 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/4-P-Series-People.webp",
    whoFor:
      "This course is designed for anyone who wants to understand the dynamics of effective communication and improve how well their messages are received. All leaders can benefit from this course.",
    overview:
      "Social scientists categorize people into four major personality styles: Power, Passion, People and Precise. During this course, you will examine how to ideally communicate with someone with the People perspective. Through adjustments in your approach, you can reduce tension and increase effectiveness when working with this personality.",
    outcomes: [
      "Recognize People style's needs and adjust your style based on their needs.",
      "Identify methods to manage tension or conflict effectively with someone with a People personality preference.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Communication Strategies for the People Personality",
        body: "In this session, we will explore how to ideally connect with and relate to someone with a People personality. We will focus on methods of communication, factors that motivate, and perceptions of the People style.",
      },
      {
        title: "Managing Tension with the People Personality",
        body: "In this session, we will examine how the People personality reacts to tension or conflict as their back-up behavior. We will discuss how someone with the People personality escalates through their Z conflict pattern as well as methods for de-escalating tension.",
      },
    ],
  },
  "4p-series-communication-from-the-power-perspective": {
    detailed: true,
    level: "Foundational",
    duration: "2 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/4-P-Series-Power.webp",
    whoFor:
      "This course is designed for anyone who wants to understand the dynamics of effective communication and improve how well their messages are received. All leaders can benefit from this course.",
    overview:
      "Social scientists categorize people into four major personality styles: Power, Passion, People, and Precise. During this course, you will examine how to ideally communicate with someone with the Power perspective. Through adjustments in your approach, you can reduce tension and increase effectiveness when working with this personality.",
    outcomes: [
      "Recognize Power style’s needs and adjust your style based on that need.",
      "Identify methods to manage tension or conflict effectively with someone with a Power personality preference.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Communication Strategies for the Power Personality",
        body: "In this session, we will explore how to ideally connect with and relate to someone with a Power personality. We will focus on methods of communication, factors that motivate, and perceptions of the Power style.",
      },
      {
        title: "Managing Tension with the Power Personality",
        body: "In this session, we will examine how the Power personality reacts to tension or conflict as their back-up behavior. We will discuss how the Power personality escalates through their Z conflict pattern as well as methods for de-escalating tension.",
      },
    ],
  },
  "4p-series-communication-from-the-precise-perspective": {
    detailed: true,
    level: "Foundational",
    duration: "2 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/4-P-Series-Precise.webp",
    whoFor:
      "This course is designed for anyone who wants to understand the dynamics of effective communication and improve how well their messages are received. All leaders can benefit from this course.",
    overview:
      "Social scientists categorize people into four major personality styles: Power, Passion, People and Precise. During this course, you will examine how to ideally communicate with someone with the Precise perspective. Through adjustments in your approach, you can reduce tension and increase effectiveness when working with this personality.",
    outcomes: [
      "Recognize Precise style’s needs and adjust your style based on their needs.",
      "Identify methods to manage tension or conflict effectively with someone with a Precise personality preference.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Communication Strategies for the Precise Personality",
        body: "In this session, we will explore how to ideally connect with and relate to someone with a Precise personality. We will focus on methods of communication, factors that motivate, and perceptions of the Precise style.",
      },
      {
        title: "Managing Tension with the Precise Personality",
        body: "In this session, we will examine how the Precise personality reacts to tension or conflict as their back-up behavior. We will discuss how the Precise personality escalates through their Z conflict pattern as well as methods for de-escalating tension.",
      },
    ],
  },
  "improve-communication-through-the-4p-personality-styles": {
    detailed: true,
    level: "All Levels",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Improve-Communications-through-the-4P-Personality-Styles-1-1.jpg",
    whoFor:
      "This course is designed for anyone that wants to understand the dynamics of effective communication and improve how well their messages are received. All leaders can benefit from this course.",
    overview:
      "Do you ever feel like you are speaking a different language when communicating with someone else? To some degree, you are! Social scientists categorize us into four major personality styles: Power, Passion, People, and Precise. During this course, you will examine your personal communication style, learn to adapt to the communication styles of your teams, discover different speaking styles, and increase employee motivation. Change your relationships by adapting your communication style and connect with others!",
    outcomes: [
      "Recognize style differences in others and adjust your style based on their needs.",
      "Build rapport using verbal and nonverbal messages.",
      "Give positive and negative feedback to different styles.",
      "Develop individual motivation approaches for others.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Miscommunication = Missed Opportunities",
        body: "We all have gaps in how we communicate. In this module, we will explore how those gaps are preventing us from achieving optimal results.",
      },
      {
        title: "The Four Personality Styles",
        body: "It is time to explore the four different personality styles in detail and understand how they process information differently. This is a FUN module! Careful, you will learn something about yourself!",
      },
      {
        title: "Style Identification",
        body: "The styles are fun to talk about, but things are about to take a serious turn! If you want to get better results, you have to start doing things differently. In this module, we are going to debunk some myths about each style, including how they prefer to communicate and what motivates each style towards action. This is really getting good!",
      },
      {
        title: "Versatility within the Styles",
        body: "So what is versatility? In this fast-paced module, we will explore how to adapt your approach with each style and identify any versatility blind spots for you. Epic.",
      },
    ],
  },
  "adapting-your-leadership-style": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Adapting-Your-Leadership-Style-.jpg",
    video:
      "https://leadershipedge.live/wp-content/uploads/2025/04/LEL_Course11-Adapting-your-Leadership-Styles.mp4",
    whoFor:
      "This course is for leaders who want to understand the different leadership styles and their implications on team members and the organization. Leaders who are interested in learning how to be adaptive by utilizing leadership and personality style knowledge to achieve collaboration and teamwork within the organization would benefit from this course.",
    overview:
      "Successful leaders must adapt to the changing environment in which they lead. Adaptive leaders move from one leadership style to another depending on the task, project, and people involved. Participants will explore the various leadership styles and learn how to match their leadership style to the needs of the people they lead. The course will dive into the four major personality styles to help leaders understand what is needed to effectively lead their team members. In this course, we will examine the use of effective communication styles from a leadership perspective to match each individual personality style of the team members within the organization.",
    outcomes: [
      "Identify leadership styles and apply appropriate leadership behaviors based on the skills and will of the individual",
      "Recognize the four major personality styles and apply communication strategies that align with each style",
      "Apply appropriate communication methods to frame messages clearly and adapt your delivery to the needs of the individual and the situation",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Communicates",
      "Diagnoses Needs",
      "Matches Style to Needs",
      "Practices Self-Awareness",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Leadership Styles and their Impact on Followers",
        body: "In this module, we will introduce the four primary styles of leadership and learn how to identify each. Leaders will understand how to recognize their primary leadership style and adapt it to become a versatile leader.",
      },
      {
        title: "Four Major Personality Styles",
        body: "There are four major personality styles that impact interaction among employees within the workplace. We will explore our own primary personality style and learn to identify other people’s personality styles in order to adapt as leaders to the needs of various team members.",
      },
      {
        title: "Methods of Communicating",
        body: "Effective communication is an essential part of being an adaptive leader. In this module we will discuss various methods of communication from a leadership perspective. We will determine how to ask the right questions, give proper feedback, frame messages, and handle problems as they arise. We will also explore how to properly utilize one-on-one communication.",
      },
    ],
  },
  "analytical-thinking-skills": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Analytical-Thinking-Skills-.jpg",
    whoFor:
      "This course is ideal for any business professional at every level who wants to improve their analytical thinking skills.",
    overview:
      "Analytical thinking skills are critical in any role, but they are especially crucial for leaders because they must solve complex problems and make effective decisions for their team and organization. Analytical thinking, or the ability to systematically and logically work through an issue, focuses on using evidence as the basis for decision-making. In this course, we will identify analytical thinking skills, explore how they impact us, and examine a six-step process of analytical thinking that will lead to effective and objective decision-making.",
    outcomes: [
      "Define the process of analytical thinking and identify the six-step analytical thinking process.",
      "Identify and utilize the first three steps of the analytical thinking process.",
      "Define and implement the last three steps of the analytical thinking process.",
    ],
    behaviors: ["Builds Trust", "Inspires", "Pursues Self-Development", "Solves Problems"],
    sessions: [
      {
        title: "Introduction to Analytical Thinking Skills",
        body: "In this module, we will define analytical thinking and its importance for you and your organization. Participants will learn the six-step analytical thinking process that is used to reach accurate conclusions when making decisions.",
      },
      {
        title: "Analytical Thinking Process, Part 1",
        body: "In this module, we will examine the first three steps of the six-step analytical thinking process: framing issues, identifying data and collecting data.",
      },
      {
        title: "Analytical Thinking Process, Part 2",
        body: "In this module, we will explore the last three steps of the six-step analytical thinking process: analyzing data, sharing analysis and applying analysis.",
      },
    ],
  },
  "the-art-of-influence": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/The-Art-of-Influence-1-scaled.jpg",
    whoFor:
      "This course is designed for team leaders and managers who wish to be more effective in their organization by learning the art of influence.",
    overview:
      "In today's corporate world, the art of influence is the language of leadership. It is a powerful tool that builds trust, establishes credibility, and connects you as a leader to your team. Influence even helps in situations where you can’t practice your formal authority. In this course, you will discover skills, strategies, and behaviors that influence others without using direct power. We will explore how to demonstrate authenticity and integrity through interactions with others and how to have a strong sense of self-awareness. You will then learn how these techniques lead to improved working relationships and increased opportunities to see your ideas grow.",
    outcomes: [
      "Define influence and understand how influence differs from controlling and manipulating.",
      "Recognize influence building and influence destroying behaviors, and utilize techniques to be more effective with others.",
      "Explore personality styles and versatility that allow us to adapt to others' styles.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Inspires",
      "Matches Style to Needs",
    ],
    sessions: [
      {
        title: "Introduction to Influencing",
        body: "In this session, we will define and explore influence, why it matters, and what impact it can have on others. We will also examine how influence is different from controlling and manipulating behavior, identify the skills and techniques of building rapport, and discover ways to improve understanding.",
      },
      {
        title: "Influence Building and Destroying Behaviors",
        body: "To learn the art of influence, we need to develop a series of learnable skills such as conflict resolution, empathy, being proactive, using neutral language, and assessing. In this session, we will identify and explore, in detail, both influence building and influence destroying behaviors. We will also discover how to implement techniques to avoid influence destroying behaviors.",
      },
      {
        title: "Personality Styles and Versatility",
        body: "This session examines how we influence others when we act in a versatile or adaptable way. We will identify the different personality styles and learn how to adjust our approach to meet the preferred style of others.",
      },
    ],
  },
  "the-art-of-small-talk-and-networking": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/The-Art-of-Small-Talk-and-Networking-1-scaled.jpg",
    whoFor:
      "This course will benefit leaders, managers, and team members who wish to improve and gain confidence in their interpersonal communication skills for business, networking, and social connection purposes.",
    overview:
      "Effective leaders must be skilled in the ability to connect with people in the business social setting. Connection is essential to building quality business relationships externally and internally. It often begins with small talk and continues with effective networking. In this course, we will explore how to tactfully communicate in a corporate social setting, build confidence by applying the art of small talk, and connect with new and known people through networking.",
    outcomes: [
      "Gain confidence in various social and business situations by applying effective networking skills.",
      "Initiate and maintain a conversation by utilizing the art of small talk.",
      "Understand electronic networking and use the best networking practices through social media.",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Pursues Self-Development",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "An Overview: The Art of Small Talk and Networking",
        body: "Small talk and networking have always been integral to building connections in the professional world, but over the years, social media has changed the way we approach relationships. In this module, we will learn how the art of small talk and networking skills are needed for interpersonal skills development and how face-to-face communication has been largely replaced by Facebook, LinkedIn, and other social media interactions. We will explore how to adapt and maximize our networking skills in-person and online.",
      },
      {
        title: "What is Small Talk?",
        body: "In this module, we will explore the components of small talk, including the skills needed to make small talk intentional with the goal of connection in mind. We will learn how to initiate a conversation, keep it going, and how to exit gracefully from the interaction at the appropriate time. Additionally, we will discuss the importance of following up with those with whom we interact.",
      },
      {
        title: "What are the Dynamics of Networking?",
        body: "In this module, we will learn how to utilize electronic networking such as social media and best practices for participation in various platforms for maximum benefit. In addition, we will dive into how to join networking groups and how to optimize the value we are getting out of these groups.",
      },
    ],
  },
  "assertiveness-skills": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Assertiveness-Skills-1-1-scaled.jpg",
    whoFor:
      "This course is ideal for leaders who want to develop assertiveness skills and become more effective communicators within their organization. This course should also be taken by anyone who wants to learn new approaches to help them achieve desired goals in a more confident and assertive way.",
    overview:
      "As a leader, it’s important to know how to use assertiveness skills for effective communication. These skills enable you to interact more effectively with your team, clients, and anyone else you interact with throughout your day. In this course, you will explore how assertiveness skills empower you to communicate more confidently and efficiently. Participants will gain insight into areas where assertiveness is necessary and utilize new techniques and methods which will have a long-lasting impact on their professional and personal life.",
    outcomes: [
      "Recognize the Assertiveness Continuum and explain the personal and professional value of assertive behavior",
      "Use the Albert Mehrabian Communication Model and the 3 Cs of nonverbal communication to communicate assertively in the workplace",
      "Apply key assertiveness techniques, including the DESC model, and respond to criticism with clarity and composure",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Inspires",
      "Pursues Self-Development",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Defining Assertiveness",
        body: "The concept of being assertive is entirely different from being aggressive. Many of us find it difficult to be assertive because we often prefer to avoid conflict. In this module, participants will be discussing the difference between passive, assertive and aggressive behavior. We will examine hot buttons or triggers that lead to aggressive behavior and complete the Assertiveness Assessment to find the right mix between confidence and consideration.",
      },
      {
        title: "Assertive Communication",
        body: "In this module, participants will examine the Albert Mehrabian Communication Model to understand how we communicate, discuss the three Cs of verbal communication, and learn how positive intent is helpful in being assertive.",
      },
      {
        title: "Active Listening",
        body: "In this module, we will examine how active listening is significant for assertiveness. Participants will also define ways to listen effectively as well as complete a listening self-assessment.",
      },
    ],
  },
  "balancing-priorities": {
    detailed: true,
    level: "Foundational",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Balancing-Priorities--scaled.jpg",
    whoFor:
      "This course is for leaders at every level who want to be effective and productive but also desire a better work/life balance. Leaders who seek to balance their priorities through efficient time management while learning how to reduce stress within the workplace would benefit from taking this course.",
    overview:
      "Leaders face conflicting tasks, deadlines, and expectations, making it difficult to identify what’s most important. Technology has become a great tool, but it has also increased the speed and complexity of our work. This course will enable you to properly prioritize your obligations, allowing you to improve performance and productivity. We will discuss the need to prioritize, set relevant goals, and use effective time management skills. We will also look at the role technology plays in helping to accomplish those goals.",
    outcomes: [
      "Understand the benefits of balancing priorities with consideration of the common obstacles that must be overcome.",
      "Create relevant and attainable goals and learn how to prioritize tasks and responsibilities.",
      "Align tools, systems and strategies to elevate personal and professional organization.",
      "Learn the skills of time management and explore eight common time-wasting activities to avoid.",
      "Understand how to utilize technology tools for better productivity.",
    ],
    behaviors: ["Demonstrates Self-Management", "Inspires", "Solves Problems", "Takes Initiative"],
    sessions: [
      {
        title: "Need for Balancing Priorities",
        body: "The ability to effectively balance priorities will not only impact your daily work routine but it will lead to better success and productivity in work and personal life. In this module, we will discuss the importance of prioritizing and learn how to handle some of the potential obstacles that may arise in the process.",
      },
      {
        title: "Setting Relevant Goals",
        body: "In this module, we will learn the steps to setting relevant goals with an emphasis on prioritizing tasks and responsibilities. Participants will understand how to delegate and how it plays a key role in the process of goal setting and balancing priorities.",
      },
      {
        title: "Organizing Yourself",
        body: "The importance of good organizational skills cannot be underestimated. In this module, participants will learn how to become organized and utilize an effective schedule to maximize the finite time they have each day.",
      },
      {
        title: "Identifying Time-Wasting Activities",
        body: "People in all levels of an organization are often unaware of how much time they are wasting each day. This module will highlight eight common time-wasting activities that often sabotage our efforts to be organized and productive in the workplace.",
      },
      {
        title: "Using Technology Effectively",
        body: "Many times, technology is an under-utilized tool when it comes to balancing priorities. In this module, we will understand how technology can be used to help us attain our goals of organization and efficiency while maintaining our priorities.",
      },
    ],
  },
  "building-authentic-relationships": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Building-Authentic-Relationships-.jpg",
    whoFor:
      "This course can be taken by anyone who is looking to build authentic relationships. Leaders who desire to enhance their professional behavior and interpersonal skills will find this course advantageous for their career.",
    overview:
      "In today’s workforce, one of the most powerful leadership skills we can have is the ability to form authentic relationships with teams and clients. Having authentic relationships enables us to develop an effective support system, work with resistance, and establish deep relationships. This course offers the opportunity to explore the skills and behaviors needed to build authentic relationships. Participants will learn to analyze their relationships and develop a practical approach for engaging team members.",
    outcomes: [
      "Define and explore concepts that build authentic relationships.",
      "Discover behaviors that win people over.",
      "Identify personal filters that create obstacles in building successful relationships.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Inspires",
      "Practices Self-Awareness",
      "Pursues Self-Development",
    ],
    sessions: [
      {
        title: "Defining Authentic Relationships",
        body: "In this module, we will define authentic relationships and understand the reasons why they are essential to success in leadership. Participants will have an opportunity to evaluate the last five interactions they had with others to discover more about their relationships. They will also explore the concept of Emotional Bank Accounts by Stephen Covey, recognize the 3 Ps of building authentic relationships, and explore the power of vulnerability.",
      },
      {
        title: "Behaviors that Enhance Authentic Relationships",
        body: "Inspirational leaders make a strong impression on others by believing in themselves and others. In this module, participants will explore the behaviors that charismatic leaders exhibit to enhance authentic relationships including warmth, body language, listening and empathy.",
      },
      {
        title: "Behaviors that Prevent Authentic Relationships",
        body: "Sometimes, in our professional life, we need to work with people we do not particularly like. However, it is essential to maintain a professional relationship with them. In this module, participants will explore the common behaviors that prevent us from building authentic relationships and discover ways we can monitor our own behavior to ensure that we have successful relationships.",
      },
    ],
  },
  "building-employee-engagement": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Building-Employee-Engagement-.jpg",
    whoFor:
      "This course is designed for leaders who are looking to keep their employees engaged and who want to get more out of themselves and their teams.",
    overview:
      "According to the recent studies, only about a third of the global workforce is highly engaged, leaving the remaining two-thirds less engaged or disengaged. Highly engaged employees are emotionally committed to their organization’s goals and use their discretionary effort to go the extra mile on behalf of their organization. This course will address the reasons employees become disengaged and provides you with strategies to increase engagement.",
    outcomes: [
      "Recognize the impact of a leader on employee engagement.",
      "Discover how building relationships and helping employees grow helps to minimize turnover.",
      "Discover strategies that increase engagement by showing employees you care and sharing their success with others.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Leverages Diversity",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "The Leader's Impact on Employee Engagement",
        body: "In this module, we will be mainly focusing on the impact that a leader has on their organization and reasons for employee departure. Participants will also examine the Employee Engagement Model strategies that increase engagement.",
      },
      {
        title: "Employee Engagement Model Part I: Know and Grow",
        body: "In this module, we will examine the first two strategies of the Employee Engagement Model: Know and Grow. These strategies offer the right amount of feedback to help employees develop and feel their work is meaningful as well as show appreciation and recognition for employees’ contributions.",
      },
      {
        title: "Employee Engagement Model Part II: Show and Crow",
        body: "This module details the next two strategies of the Employee Engagement Model: Show and Crow. These strategies help identify employees’ strengths and leverage them in the workplace as well as show support and remove obstacles that hinder their ability to do so.",
      },
    ],
  },
  "business-etiquette": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Business-Etiquette-.jpg",
    whoFor:
      "This course is recommended for business leaders, senior executives, managers, supervisors, and front-line workers.",
    overview:
      "Traditional business relationships and communication methods are changing; however, etiquette and professionalism are essential skills when interacting with team members, internal customers, and external clients. Whether it is being polite to a rude co-worker, keeping a clean workspace, or using proper grammar in an email, these courtesies make the workplace more enjoyable. In this course, we will dive into the importance of proper business etiquette and explore techniques to use when dealing with people who cause stress. We will also define ways to turn people down diplomatically, examine the impact of human connection, and discover the importance of being a respected leader.",
    outcomes: [
      "Define and understand business etiquette and its importance.",
      "Recognize methods to build rapport that enhance relationships.",
      "Discover the importance of being a respected leader.",
    ],
    behaviors: ["Builds Trust", "Demonstrates Self-Management", "Inspires"],
    sessions: [
      {
        title: "Manners and Courtesy Matter",
        body: "In this session, we will define business etiquette and its importance, discuss how etiquette and professionalism go hand in hand, and identify how patience in the workplace fosters good relationships. Finally, we will discover how to handle people who increase your stress levels and how to say “no” diplomatically.",
      },
      {
        title: "Building Rapport for Better Relationships",
        body: "In this session, we will relate how human connections play an important role in business etiquette and recognize the importance of positive language and listening skills in your daily work routine. We will also explore two concepts: the power of what is not said and how actions speak louder than words.",
      },
      {
        title: "Being a Respected Leader",
        body: "In this session, we will identify the qualities respected leaders have in common. We will also examine how encouraging employees and building trust will help you to become a respected leader within your organization.",
      },
    ],
  },
  "competency-and-behavioral-based-interviewing": {
    detailed: true,
    level: "All Levels",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Competency-and-Behavioral-based-Interviewing-1-1-scaled.jpg",
    whoFor:
      "This course is appropriate for individuals involved in the recruiting, interviewing, and hiring processes.",
    overview:
      "Hiring quality employees is one of the most critical tasks in any organization. The basis of effective interviewing is asking the right questions. In this program, participants will learn the art of interviewing systematically to get the information that is most critical for job performance and ensure the working partnership is mutually beneficial. Participants will be able to identify competencies associated with the job, conduct interviews, and practice recruiting and evaluating candidates.",
    outcomes: [
      "Identify competencies to screen for.",
      "Develop behavioral-based questions.",
      "Conduct a behavioral interview effectively.",
      "Avoid legal pitfalls.",
      "Evaluate candidates’ responses objectively.",
      "Make confident hiring decisions.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Focuses on Results through People",
      "Innovates",
      "Leverages Diversity",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Introduction to Behavioral Interviewing",
        body: "The interview is the most important step in the hiring process. The goal of the interview is to identify and select candidates whose skills, experience, and behaviors align with the job role. Moreover, you want to make sure the candidate’s personality, interests, and values match the culture and mission of the organization. In this module, we discuss techniques you can use to ensure fair and accurate interviewing, evaluation, and selection.",
      },
      {
        title: "Defining the Job and Candidate",
        body: "One of the first tasks on the “To Do” list for hiring new employees is to determine the qualifications and characteristics of the ideal candidate. This is not an easy task; however, the behavioral interviewing process is specifically designed to simplify it. In this module, we provide you with a competency-based approach to identify the requirements for the job.",
      },
      {
        title: "Interview Questions",
        body: "The goal of behavioral interview questions is to gain information about the candidate’s past experience that reflects the job-related competencies. In this module, we use a hands-on approach to help you develop practical behavioral interview questions. Based on the S.T.A.R. method, you will create questions that encourage candidates to respond in a story form. We will also provide you with tools and approaches to help you evaluate the candidate’s responses.",
      },
      {
        title: "The Interview",
        body: "Preparing for and conducting the interview is another critical task. In addition to having a detailed understanding of the position, competencies necessary for success, questions, and evaluation scale, interviewers must be clear on what is expected of them in their role. In this module, we discuss the details of the interview process, including making the candidate comfortable, listening, note-taking, and more. We also provide you with best practices to ensure a successful interview for candidates and interviewers.",
      },
      {
        title: "Using Technology Effectively",
        body: "Making the decision to hire, or not hire, a candidate is critical. Many factors must be carefully considered and evaluated before the hiring decision is made. In this module, we discuss in detail the ins and outs of the decision-making process. We also provide you proven, practical approaches to ensure the process is fair, legal, accurate, and thorough to select the best candidate for the job.",
      },
    ],
  },
  "collaborative-coaching-conversations": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Collaborative-Coaching-Conversations-1-1-scaled.jpg",
    whoFor: "Anyone who manages others.",
    overview:
      "Collaborative Coaching Conversations is a skill-based training course that focuses on teaching leaders a coaching mindset, and three coaching conversation skills— listening, meaningful recognition and asking questions.",
    outcomes: [
      "Describe the mindset and impact of collaborative coaching conversations.",
      "Identify the types of questions that create transformation and change.",
      "Identify the inner and outer practices needed to listen effectively.",
      "Identify values or qualities and differentiate methods of recognition.",
    ],
    behaviors: [
      "Builds Trust",
      "Communicates",
      "Focuses on Results through People",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Introduction to Collaborative Coaching Conversations",
        body: "In this module, we will illustrate how coaching can empower others to step up their game to be more effective and accountable, solve challenges, and understand others in a powerful way. We will also define what a coaching conversation is and how it differs from other types of conversations.",
      },
      {
        title: "Skill One: Asking Questions",
        body: "In coaching, powerful questions knock people off their automatic pilot program and make them fly the airplane. In this module, we will identify and practice asking the questions that create transformation and change.",
      },
      {
        title: "Skill Two: Listening",
        body: "In this module, we will identify the best listening skills (inner and outer) used in coaching conversations.",
      },
      {
        title: "Skill Three: Giving Meaningful Recognition",
        body: "In this module, we will identify meaningful recognition and define its importance in coaching conversations. We will name values or qualities and understand two ways, acknowledgment and appreciation, to honor the successful person in front of you.",
      },
    ],
  },
  "competitive-analysis": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Competitive-Analysis-.jpg",
    whoFor:
      "This course is ideal for leaders, managers, and market analysts who need to better understand their competition and be ready to dominate and take over their market.",
    overview:
      "There is intense competition in today’s market. It’s important to be fully aware of your competition and analyze their strategies to determine their strengths and weaknesses. This will provide an opportunity to develop your business strategies one step ahead of your competitors and stand out in your industry. This course will help you understand the complete competitive marketing environment, your competitors’ approach, and what is working for them. We will explore all aspects of the competitive analysis tools and learn how to be in a place of advantage when compared to your competitors. By the end of this course, you will be able to associate threats and opportunities associated with new market trends, look for the gaps within your organization, and develop a priority plan to fill those gaps.",
    outcomes: [
      "Define competitive analysis and its importance.",
      "Identify and utilize competitive analysis tools.",
      "Recognize SWOT analysis and the best way to utilize it.",
      "Define the gaps within your organization and develop a priority plan to close the gaps.",
    ],
    behaviors: ["Sets Goals", "Solves Problems", "Takes Initiative"],
    sessions: [
      {
        title: "The What, Why, and Who of Competitive Analysis",
        body: "In this module, we will define competitive analysis, why it’s done, and who should do it. We will also explore what a competitive analysis looks like from a large company scale, a department scale, and a personal scale.",
      },
      {
        title: "Outlining the Competitive Landscape",
        body: "This module examines several competitive analysis tools including a competition analysis, marketplace analysis, and stakeholder analysis. We will understand these tools and know how to use each of them.",
      },
      {
        title: "SWOT Analysis",
        body: "This module focuses on how to compare the weaknesses of your competitor against your strengths and vice versa. We will define SWOT analysis, explore its importance, and analyze your gathered data to uncover the opportunities and threats within your target market.",
      },
      {
        title: "Gap Analysis and Priority Planning",
        body: "Once you have the entire competitive landscape and SWOT analysis, the next step is to perform a gap analysis based on that data. In this module, we will examine the gap analysis and determine how to close the gaps. We will explore how to prioritize and plan the efforts associated with the gap analysis.",
      },
    ],
  },
  "connecting-across-cultures": {
    detailed: true,
    level: "Foundational",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Connecting-across-Cultures-.jpg",
    whoFor:
      "This course is designed for individuals who interact frequently with people from different cultures.",
    overview:
      "In this course, you will learn techniques to connect with people from different cultures. These cultural concepts will greatly impact your professional success and relationships with individuals from different cultural backgrounds. The content of this course is based on years of research from cultural experts around the world. Upon completion of the course, you will understand how to culturally connect with others, drive greater results and improve cross-cultural relationships. You can expect to learn how cultural values affect everyone’s attitudes and behavior and how to interpret other people’s actions through a cultural lens.",
    outcomes: [
      "Determine how culture influences perceptions, values, attitudes, communication style, and behavior of others.",
      "Analyze the impact of the level of individualism vs collectivism within a culture and determine how that is displayed in the workplace.",
      "Conclude when there is a difference among individuals’ perceptions on how power is distributed within",
      "organizations",
      "Identify a culture’s priority on time vs. relationships and the cultural drivers behind what is seen as right vs. wrong.",
      "Recognize differences in cultural communication styles and a culture’s work vs. life priorities.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Practices Self-Awareness",
      "Demonstrates Self-Management",
      "Leverages Diversity",
    ],
    sessions: [
      {
        title: "The Influence of Culture",
        body: "During this kickoff module, we will explore the impact our culture has on our behavior and perceptions. We can increase productivity and decrease tension in our key relationships through careful consideration of our own culture and the cultural values of others.",
      },
      {
        title: "Individualism vs. Collectivism",
        body: "During this module, we will explore a cornerstone set of cultural values: Individualism vs. Collectivism. Within cultures that have a stronger focus on Individualism, members will tend to focus on individual goals and the rights of the individual person. In Collectivistic cultures, members tend to prefer group goals, what is best for the collective group, and personal relationships.",
      },
      {
        title: "Power Distance & Uncertainty Avoidance",
        body: "Different cultures view the role of authority differently. Some cultures value hierarchy and structure and other cultures value equality and openness. In this session, we will explore the cultural implications of the amount of direction people expect from their leaders and the need for eliminating ambiguity.",
      },
      {
        title: "Concept of Time & Universalism vs. Particularism",
        body: "Time is a universally accepted concept but viewed and managed very differently depending on cultural values. During this session, we will explore the cultural viewpoints of time and how they impact relationships and results. We will also explore how cultures see different definitions of what is right or wrong.",
      },
      {
        title: "Communication Styles & Activity Orientation",
        body: "Have you ever tried communicating with someone from a different culture and it seemed like you were not connecting? It is possible that cultural communication challenges were at play in the conversation. During this session, we will explore typical communication approaches for different cultures and how to adjust your style to meet the needs of others.",
      },
    ],
  },
  "conquering-conflict": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Conquering-Conflict-.jpg",
    whoFor:
      "This course is designed for team leaders looking to feel more confident in discussing issues and problems with their team. Participants will acquire the skills, knowledge and confidence to efficiently conquer conflict situations in the workplace, in a clear, assertive and constructive way.",
    overview:
      "In the challenging corporate world, conflicts are common and frequent. These conflicts, such as differences of opinion, not living up to expectations, denial, and lack of cooperation can lead to serious consequences for a leader. This course is designed to help leaders understand their role in conflict situations, and how to adapt to the situation to achieve better outcomes. Participants will learn conflict management skills including practical communication techniques, effective listening skills and approaches to avoid escalating problems.",
    outcomes: [
      "Define key principles associated with conflict management.",
      "Identify the two types of conflict and psychological safety methods that can help respond effectively to challenging people.",
      "Utilize effective power listening skills to minimize conflict and assist others to communicate productively.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Matches Style to Needs",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "What is Conflict?",
        body: "In this module, we will define the key principles around conflict management, identify the relationship between the chain of dialogue and conflict, and closely examine a guide to styles of handling conflict.",
      },
      {
        title: "Conflict Dialogue",
        body: "Leaders feel more empowered when they can handle difficult conversations and conflict situations. They are able to tackle conflicts at an early stage if they are familiar with conflict management concepts. In this module, participants will recognize and understand the two types of conflicts, the psychological safety principle, and effective communication methods that help teams to have productive dialogues.",
      },
      {
        title: "Communication Skills During Conflicts",
        body: "In this module, participants will explore the essential communication skills that are needed to handle conflicts and identify the AMPP skills which enable effective communication.",
      },
    ],
  },
  "creating-a-culture-of-trust": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Creating-a-Culture-of-Trust-.jpg",
    whoFor:
      "This course is for leaders who want to create a culture of trust, develop a positive work environment, build stronger teams, and increase productivity in their organization.",
    overview:
      "Trust is the critical link in building strong personal and professional relationships. Creating a culture of trust is essential in any organization. Developing this trust allows teams to become stronger, increase productivity and create a positive work culture. In this course, we will define the culture of trust in your organization, identify behaviors of trust and determine how to build an environment of trust.",
    outcomes: [
      "Define the culture of trust, characteristics of trustworthy people, and trust factors.",
      "Distinguish behaviors that destroy trust and those that build trust.",
      "Determine methods of building an environment of trust and restoring it when broken.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Focuses on Results through People",
    ],
    discussionQuestions: [
      {
        prompt: "What does someone do here that makes you trust them, without them saying a word?",
        facilitatorNote:
          "Good answers are behaviors, not traits: they follow up, they say when they are wrong, they keep things you told them private.",
        probe: "Think of someone you would go to with bad news first. What do they do?",
        minutes: 6,
        behaviors: ["builds-trust", "practices-self-awareness"],
      },
      {
        prompt: "What is the fastest way to lose trust on a team like ours?",
        facilitatorNote:
          "Listen for small repeated things, missed commitments, information held back, credit taken, rather than one dramatic betrayal.",
        probe: "Does it usually break in one moment, or wear down slowly?",
        minutes: 6,
        behaviors: ["builds-trust", "communicates"],
      },
      {
        prompt: "How safe is it here to say I do not know, or I got that wrong?",
        facilitatorNote:
          "The room may go quiet, and that answer is itself data. Do not fill the silence. Let it sit, then ask the probe.",
        probe: "When did someone last admit a mistake in a meeting, and what happened next?",
        minutes: 8,
        behaviors: ["collaborates-and-promotes-teamwork", "demonstrates-self-management"],
      },
      {
        prompt: "Where do we most need more trust to do our work well?",
        facilitatorNote:
          "Push for a specific handoff, meeting, or decision point rather than a general feeling about the team.",
        probe: "Which handoff between us takes the most double-checking?",
        minutes: 7,
        behaviors: ["focuses-on-results-through-people", "diagnoses-needs"],
      },
      {
        prompt: "What is one thing we could start doing next week that would build trust here?",
        facilitatorNote:
          "Aim for something the team controls without permission from anywhere else, and small enough to still be happening in a month.",
        probe: "What would we need to stop doing to make room for it?",
        minutes: 7,
        behaviors: ["champions-change", "takes-initiative"],
      },
    ],
    sessions: [
      {
        title: "Defining Culture of Trust",
        body: "In this session, we will define trust and discover how it impacts us individually, as a team and as an organization. We will compare trust and trustworthiness, interpret the impact of having low trust in an organization, and identify various trust factors.",
      },
      {
        title: "Behaviors that Define Trust",
        body: "In this session, we will identify behaviors that build or destroy trust and their impact on individuals, teams, and organizations. Participants will also examine the concept of the emotional bank account and its benefits.",
      },
      {
        title: "Building an Environment of Trust",
        body: "In this session, participants will identify how to build an environment of trust so that it can have a positive impact throughout the organization. We will examine the role psychological safety plays in a trusting culture and techniques to build back trust when it is broken.",
      },
    ],
  },
  "creative-problem-solving": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Creative-Problem-Solving-1-1-scaled.jpg",
    whoFor:
      "This course is for leaders who directly or indirectly make decisions that impact their teams and their organizations and want to be efficient, productive, and effective while overcoming obstacles and challenges with creativity.",
    overview:
      "Creative problem solving identifies opportunities for solving problems where conventional thinking has failed. Leaders are encouraged to find new perspectives and create innovative solutions to problems that inevitably arise. In this course, we will learn the difference between creativity and innovation and how leaders can foster an environment of innovation, which allows for creative problem solving.",
    outcomes: [
      "Define and distinguish the difference between creativity and innovation and analyze how perception influences the way problems and opportunities are interpreted",
      "Identify workplace conditions that encourage creativity and evaluate how leaders and teams can support innovative thinking",
      "Apply structured creative thinking tools to analyze problems and generate practical solutions",
    ],
    behaviors: ["Diagnoses Needs", "Inspires", "Solves Problems"],
    sessions: [
      {
        title: "Difference Between Creativity and Innovation",
        body: "In this module, we will define and understand the difference between creativity and innovation, while challenging our thinking and perceptions. We will also understand how different internal and external factors impact our perception and how our perception affects creativity.",
      },
      {
        title: "Creating an Environment of Innovation",
        body: "In this module, we will explore how the workplace environment may encourage or inhibit the creativity and innovation of employees. We will also define the Fifteen Percent Rule and how it impacts employees, as well as explore the importance of having an Idea Sponsor and the process of capturing ideas.",
      },
      {
        title: "Tools for Creative Problem Solving",
        body: "In this module, we will introduce various tools leaders can use for creative problem solving including Mind Maps, Analogies, Fish Bone Diagram, Affinity Diagram, SCAMPER, Sticky Notes, Brainstorming, and more. We will learn how to apply these tools to situations that arise in the workplace.",
      },
    ],
  },
  "customer-service-excellence": {
    detailed: true,
    level: "Intermediate",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Customer-Service-Excellence-.jpg",
    whoFor:
      "This course is ideal for service managers, customer service representatives or any business professional who wants to enhance their customer service skills.",
    overview:
      "Have you ever wondered what happened to good customer service? Do you want your department to be something that people rave about? It all starts with customer service. It is important that your customers feel that they are respected and appreciated. In this course, you will discover how our attitudes affect us professionally and how they can make or break any experience for our customers. The course will enable you to practice effective listening skills, monitor body language and understand how to deal with difficult people. This will help develop an excellent customer service relationship with your customers that will increase repeat business.",
    outcomes: [
      "Recognize how attitude and service are related within the Service Model.",
      "Identify the key elements of effective communication in the customer relationship.",
      "Utilize the trust equation in our daily relationships.",
      "Apply techniques when dealing with difficult people and situations.",
    ],
    behaviors: ["Communicates", "Diagnoses Needs", "Pursues Self-Development"],
    sessions: [
      {
        title: "The Service Model",
        body: "Service is a never-ending story that happens every day and it is our job to discover how we can exceed our customer’s expectations. In this module, we will explore the Service Model and how it works in the workplace, and we will identify the proper attitude of service and methods of maintaining it.",
      },
      {
        title: "Clear and Consistent Communication",
        body: "This session will explore the key elements of effective communication in the customer service relationship, including how to be an active listener and adapt to specific communication styles.",
      },
      {
        title: "The Trusted Adviser",
        body: "In this module, we will define trust and the benefits that come when the customer trusts you more. Participants will recognize how trust and trustworthiness are related and learn how to apply the trust equation to their daily relationships. We will also explore Emotional Bank Accounts and examine the concepts of deposits and withdrawals.",
      },
      {
        title: "Dealing with Difficult People",
        body: "Most of the time, when we struggle in dealing with difficult people, it’s not because they are difficult, but because they are different from us. In this module, we will identify the strategies for successfully handling difficult situations, and we will explore the LAST methodology when there is a misalignment of expectations.",
      },
    ],
  },
  "delegating-for-development": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Delegating-for-Development-.jpg",
    whoFor:
      "This course is ideal for leaders who want to maximize the use of their time by learning how to grow their team, foster creativity, and strengthen their organization with efficiency.",
    overview:
      "Delegation is about entrusting responsibility and accountability to others. By delegating tasks, a leader is releasing some decision-making and authority to his or her subordinate, while at the same time keeping responsibility for the completion of the task. Those who implement effective delegation will improve quality of work, create personal accountability, and motivate their employees. This leads to more flexibility in assignments and increased productivity. Participants of this course will understand the need for delegation and learn how to delegate effectively.",
    outcomes: [
      "Explain the importance of delegation and evaluate its impact on individual and organizational effectiveness",
      "Demonstrate how to effectively delegate tasks by selecting appropriate responsibilities, assigning them clearly, and setting expectations",
      "Apply the key steps of effective delegation to assign tasks and monitor progress appropriately",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Focuses on Results through People",
      "Inspires",
    ],
    discussionQuestions: [
      {
        prompt:
          "What is one task you are holding onto right now that somebody else on this team could do?",
        facilitatorNote:
          "A good answer is specific and slightly uncomfortable. People usually name the task they are fastest at, which is exactly the one that should move.",
        probe: "Think about last week. What did you do twice that nobody else knows how to do?",
        minutes: 6,
        behaviors: ["focuses-on-results-through-people", "practices-self-awareness"],
      },
      {
        prompt: "When work gets handed back to you half done, what usually went wrong at handover?",
        facilitatorNote:
          "Listen for vague instructions, no deadline, no definition of finished, or no agreement on how much authority came with the task.",
        probe: "Did the person know what finished was supposed to look like?",
        minutes: 7,
        behaviors: ["communicates", "sets-goals"],
      },
      {
        prompt:
          "How much decision-making do you usually pass along with a task, and how do you say it out loud?",
        facilitatorNote:
          "Strong answers name a level: decide on your own, decide and tell me, bring me two options, ask me first. Weak answers assume the other person will guess.",
        probe: "Has anyone here checked in more than the task needed because they were not sure?",
        minutes: 7,
        behaviors: ["collaborates-and-promotes-teamwork", "matches-style-to-needs"],
      },
      {
        prompt: "What gets in the way of delegating here, honestly?",
        facilitatorNote:
          "Expect time pressure, quality worry, and workload guilt. All three are real. The useful part is which one shows up most on this team.",
        probe: "Is it faster to do it yourself, or does it just feel faster?",
        minutes: 6,
        behaviors: ["demonstrates-self-management", "solves-problems"],
      },
      {
        prompt:
          "Pick one thing you will hand over this month, and say what support you will give with it.",
        facilitatorNote:
          "Push for a named task, a named person, and a check-in point. Support without a check-in point is a wish.",
        probe: "What would make you step in early, and how will you resist that?",
        minutes: 6,
        behaviors: ["inspires", "takes-initiative"],
      },
    ],
    sessions: [
      {
        title: "The Need for Delegation",
        body: "Delegation is helpful, not only to you, but to the organization and the employee. In this module, participants will understand what effective delegation looks like and why it is beneficial, while discussing what tasks are appropriate to delegate and common barriers they may encounter.",
      },
      {
        title: "How to Delegate",
        body: "In this module, we will identify methods of matching people to tasks or projects, recognize things to avoid in delegation, and discover how to avoid reverse delegation.",
      },
      {
        title: "Steps of Delegation",
        body: "Understanding the steps to delegation is essential. In this module, we will explore the levels of autonomy, communication expectations, and types of support. We will also address how to anticipate problems, learn from mistakes, and apply the after-action review to become more effective in delegation.",
      },
    ],
  },
  "developing-a-growth-mindset": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Developing-a-Growth-Mindset-1-1-scaled.jpg",
    whoFor:
      "This course is ideal for anyone who wants to change their mindset, embrace learning, foster persistence and resilience, and make a positive impact in their own life, team and organization.",
    overview:
      "Those who have a growth mindset believe that they are in control of their own abilities, such as talent and intelligence, and that those abilities can be developed or improved through hard work. Research indicates that leaders can encourage their employees to adopt a growth mindset by changing how they interact with them. Employees with a growth mindset are constantly trying to learn and grow to better themselves. In this course, we will examine the growth mindset, how to teach it, and how to move towards it. We will also differentiate between a growth mindset and a fixed mindset and explore the impact a growth mindset can have on teams and organizations.",
    outcomes: [
      "Define and differentiate between a fixed mindset and a growth mindset.",
      "Examine the benefits and steps needed to move towards a growth mindset.",
      "Identify the impact that a growth mindset has on your team and organization.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Pursues Self-Development",
    ],
    sessions: [
      {
        title: "Introduction to Growth Mindset",
        body: "In this module, we will define a growth mindset and a fixed mindset. We will also evaluate the difference and analyze the impact both of these mindsets can have on individuals and organizations.",
      },
      {
        title: "Retraining your Brain towards a Growth Mindset",
        body: "Mindsets can be changed and shifting mindsets can have a profound effect on nearly every aspect of one’s life. In this module, we will explore how we can teach others to move towards a growth mindset and the steps needed to do so.",
      },
      {
        title: "Growth Mindset in Teams and Organizations",
        body: "In this module, we will explore the impact of developing a growth mindset within our teams and organizations by identifying the guidelines needed to accomplish this and analyzing how to weave the guidelines into your daily life.",
      },
    ],
  },
  "developing-dynamic-teams": {
    detailed: true,
    level: "All Levels",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Developing-Dynamic-Teams-1-1-scaled.jpg",
    whoFor:
      "This course is appropriate for anyone who leads onsite or remote teams. Although the course is geared toward team leaders, it is also appropriate for others in the organization involved in team-building activities such as human resources professionals, coaches, and organization development specialists.",
    overview:
      "It is difficult to argue against the fact that a team can accomplish more than any one individual. Yet, across many organizations we have dysfunctional teams, processes that lead toward competition, and attitudes of apathy towards teamwork, which directly impact productivity and quality. In the Developing Dynamic Teams course, participants learn strategies to align their teams to work collaboratively toward common objectives, and implement processes that maximize team effectiveness and communication. Once the team is aligned with a powerful objective and obstacles have been removed, the results will speak for themselves.",
    outcomes: [
      "Build team alignment by creating a shared vision, clear values, and collective direction",
      "Develop meaningful team goals and mission-focused action plans that drive accountability and performance",
      "Leverage individual strengths to improve collaboration, engagement and overall team effectiveness",
      "Apply measurement, calibration and coaching strategies to sustain team performance and continuous progress",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Innovates",
      "Inspires",
      "Leverages Diversity",
      "Pursues Self-Development",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Team Mission Planning",
        body: "A team without direction is like a rudderless ship. In this module, we discuss the overall mission and direction of a team. The topics covered are the communication characteristics of high-performing teams, establishing the team’s value, contribution expectations, and creating the team’s mission statement.",
      },
      {
        title: "Team Goals",
        body: "Once the team’s mission is determined, and you know what you want to achieve, the next step is setting goals. When teams have challenging, meaningful goals to work towards, they come together as a more effective and collaborative unit. In this module, we learn to focus on goals and results, recognize and avoid common goal-setting mistakes, and create goals that align with the team’s mission statement.",
      },
      {
        title: "Assess Strengths",
        body: "Teams benefit when individual members are fully aware of their strengths and weaknesses; however, leaders must understand the critical attributes of the team as a whole. In this module, we discuss assessing individual strengths to ensure the best possible team performance, aligning individual strengths with team goals, and building opportunities to develop and enhance each team member’s strengths.",
      },
      {
        title: "Measurement and Motivation",
        body: "At the most rudimentary level, an effective team is defined as one that has an impact above and beyond the individual contributions of any one team member. This above and beyond impact is measured at multiple levels: intangible performance, structural and relational metrics, and business results. Moreover, motivation is the bow that ties the entire package together. When you combine the energy, knowledge, and skills of a motivated team of people, you and your team can succeed. Topics covered in this module include establishing metrics for each team goal, methods for communicating progress, and implementing innovative ways to motivate teams.",
      },
    ],
  },
  "developing-others-your-top-priority": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Developing-Others-Your-Top-Priority-1-1-scaled.jpg",
    whoFor:
      "This course is for leaders who manage others and are enthusiastic about developing their team members. It will help participants to play an effective role in their team members’ growth within the organization, giving them the knowledge and skills needed to develop people productively.",
    overview:
      "Leaders who provide continual development for their team members stand above the rest. In this course, we will discuss how to create an environment that supports the development of your team members. Developing your team’s skills will enable them to be more effective, take on more challenges, and ultimately become more productive for you and the organization. This course will teach participants the importance of team development, how to start having effective development conversations with their team, and how to extend support to team members in their development.",
    outcomes: [
      "Define the importance of a leader’s role in developing others while utilizing the strength-based approach and creating development goals.",
      "Identify and demonstrate effective Development Conversations and Performance Conversations.",
      "Employ a growth mindset when developing others.",
    ],
    behaviors: [
      "Builds Trust",
      "Focuses on Results through People",
      "Inspires",
      "Pursues Self-Development",
    ],
    sessions: [
      {
        title: "Developing Others",
        body: "It is essential that leaders recognize the impact development efforts for your team members have on the entire organization. In this module, we will discuss a leader’s responsibility for developing others, defining the importance of a strength-based approach, as well as completing and analyzing the VIA Strengths assessment.",
      },
      {
        title: "The Development Conversation",
        body: "This module will define development conversations and demonstrate how to have them with your team members. We will also compare the difference between a development conversation and a performance conversation.",
      },
      {
        title: "Supporting the Development of Others",
        body: "In this module, we will identify ways to support the development of your team through building a growth mindset. We will also explore alternative methods for developing others.",
      },
    ],
  },
  "developing-positive-relationships-at-work": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Developing-Positive-Relationships-at-Work.jpg",
    whoFor:
      "This course is ideal for anyone who wants to have healthy relationships in the workplace, allowing them to be more effective and productive, while maintaining a positive and encouraging work environment.",
    overview:
      "It is vital that employees feel connected and supported by subordinates, peers, and leaders. Employees with good working relationships handle conflicts effectively and treat each other with mutual respect. When the work environment promotes this kind of culture, employees have higher morale and productivity. In this course, we will explore the impact of positive intentions and assumptions and examine methods to build positive relationships. We will identify strategies for interacting with others that will help you to develop a supportive, encouraging, and positive environment in your workplace.",
    outcomes: [
      "Discover the importance your mentality has on developing positive relationships.",
      "Identify methods to build positive relationships.",
      "Define and utilize strategies for positive interactions with others.",
    ],
    behaviors: ["Builds Trust", "Communicates", "Solves Problems"],
    sessions: [
      {
        title: "The Mental Game of Developing Positive Relationships",
        body: "This module will highlight the powerful role your mentality plays in building strong relationships. In this module, you will recognize the impact positive intentions and assumptions about others have on relationships. We will also explore the importance of thinking before reacting to a situation and examine how establishing trust will enable you to have the best positive relationships in your workplace.",
      },
      {
        title: "Building a Positive Relationship with Others",
        body: "In this module, we will identify methods to build positive relationships. We will explore how clear communication and discussing your expectation will have a positive effect on relationships. Then, we will examine how building trust, showing respect, and being loyal to those who are absent positively impact relationships.",
      },
      {
        title: "Interacting with Others",
        body: "In this module, we will learn how showing empathy, finding commonality, and giving credit where it’s due all benefit healthy relationships. We will also discover how to face differences directly, but with respect, while maintaining strong relationships in the workplace.",
      },
    ],
  },
  "development-to-delegation-cycle": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Development-to-Delegation-Cycle.jpg",
    whoFor:
      "This course is appropriate for leaders who seek to support and develop their team members and enhance their effectiveness and commitment to the organization. Leaders who want to foster personal and professional growth in their employees will benefit from taking this course.",
    overview:
      "Leaders must be flexible in their approach and know when to apply leadership skills with intentionality to obtain the maximum growth and effectiveness of individual employees and, by extension, the organization. In this course, participants will understand and utilize the model of the Development to Delegation Cycle and its Leadership Behaviors (Direction and Dialogue). They will then take this knowledge and apply the correct behavior to match the four different Task Levels of the employee at a given point in their professional development.",
    outcomes: [
      "Define the Development to Delegation Cycle and how it should be utilized.",
      "Discuss and understand the four Task Levels and determine the levels of support employees need at each level.",
      "Examine which Leader Behaviors are appropriate for each Task Level and practice leadership flexibility, providing fluid growth among team members.",
    ],
    behaviors: ["Builds Trust", "Focuses on Results through People", "Inspires", "Sets Goals"],
    sessions: [
      {
        title: "Introduction of Development to Delegation Cycle",
        body: "In this module, we will define the Development to Delegation Cycle and the Task Levels attributed to various points in an employee’s stage in professional development. We will then examine the Leadership Behaviors and how to match each to the team member’s needs.",
      },
      {
        title: "Task Levels by Determining Skill and Will",
        body: "In this module, we will discuss and understand the four Task Levels of your team by determining “Skill and Will.” We will also examine how to resolve a disagreement about Task Levels.",
      },
      {
        title: "Adjusting Your Leadership Behavior",
        body: "In this module, we will dive into the four different Leadership Behaviors and how they relate to the Task Levels of employees. We will analyze the Direction Leadership Behavior and Dialogue Leadership Behavior and determine when to apply each.",
      },
    ],
  },
  "driving-results-through-goal-setting": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Driving-Results-through-Goal-Setting.jpg",
    whoFor:
      "This course is ideal for any leader or business professional who wants to set effective goals with a focused approach and achieve the best results, personally and within their organization.",
    overview:
      "If you want to succeed, you need to set goals. When you set goals, you take control of your life’s direction. Setting goals gives you focus and direction, increases your happiness, and significantly benefits your team and organization. In this course, we will explore the process to set SMART goals that will provide the best results possible for yourself, your team, and your organization.",
    outcomes: [
      "Define and explore goal setting and the process of creating SMART goals.",
      "Recognize how to deliver goals the right way and identify your role as a leader.",
      "Examine the importance of monitoring your goals.",
    ],
    behaviors: ["Demonstrates Self-Management", "Focuses on Results through People", "Sets Goals"],
    sessions: [
      {
        title: "Goal Setting Process",
        body: "Goal setting is not just needed for us and our development as leaders. It is also a development effort for others. In this module, we will define and explore SMART goals, their importance, and the process of creating them. We will also understand next level leadership goal setting which includes past performance, present needs, and planning for the future. Finally, we will examine the kryptonite of effective goal setting.",
      },
      {
        title: "Goal Delivery",
        body: "Once SMART goals are established, we need to look at the parameters we can document during the goal-setting process. In this module, we will identify and relate the SMT parameters and the AR we consider in order to create the best goals. We will also identify our role in goal setting and determine the balance between direction and dialogue.",
      },
      {
        title: "Monitor Your Goals",
        body: "Monitoring the workload of your team members plays an important role in executing goals. In this module, we will determine how you can monitor your team members’ goals, monitor the workload of your team, and identify timelines for reviewing the goals and making necessary changes. We will also recognize commonly missed steps in goal setting.",
      },
    ],
  },
  "emotional-and-cognitive-empathy": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Emotional-and-Cognitive-Empathy-1-1-scaled.jpg",
    whoFor:
      "This course is for all leaders who want to develop teams that are loyal, engaged, and motivated. Participants can expect the learned behaviors of empathy from this course to help them gain maximum efficiency from employees and build trust with clients.",
    overview:
      "Empathy is an essential leadership skill that is often underutilized by today’s leaders. Emotional and cognitive empathy are needed to effectively lead and manage teams, relate to clients, and interact with people at all levels of an organization. In this course, we will learn to recognize and employ empathy and understand how it impacts our personal and professional lives, highlighting some of the pitfalls that can arise when it is underutilized. We will use case studies to identify empathetic behaviors and learn how they can benefit relationships within the professional setting.",
    outcomes: [
      "Define emotional and cognitive empathy and learn how sympathy is an ineffective substitute for empathy.",
      "Identify empathetic behaviors and how they are useful in the workplace.",
      "Practice empathy by participation in case studies.",
    ],
    behaviors: ["Builds Trust", "Focuses on Results through People", "Inspires", "Solves Problems"],
    sessions: [
      {
        title: "Introduction to Empathy",
        body: "Successful leadership requires the skill of empathy, but it doesn’t always come naturally, and it is a learned competency. In this module, we will define and understand the skill of empathy and discuss how it differs from sympathy. We will also distinguish the difference between emotional empathy and cognitive empathy and explore the impact empathy has on relationships.",
      },
      {
        title: "Exploring Empathic Behaviors",
        body: "In this module, we will identify and understand the impact of various empathic behaviors including developing an inward attitude, conveying personal warmth, active listening, trustworthiness, and turning conflict into cooperation.",
      },
      {
        title: "Practicing Empathy",
        body: "In this module, participants will practice using the empathetic behaviors learned in Module 2 by participating in interactive case studies. This will give each person an opportunity to try out their knowledge and gain feedback on how well they implement emotional and cognitive empathy.",
      },
    ],
  },
  "emotional-triggers": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Emotional-Triggers-.jpg",
    whoFor:
      "This course is for anyone who seeks to recognize their emotional triggers and learn how to manage them effectively.",
    overview:
      "Have you found yourself in a situation where something happens that brings forth a bad memory or emotional feeling? While this may not impact others at all, you find yourself reacting negatively in that moment or even the rest of the week. These are emotional triggers. This course will help you identify your emotional triggers, acknowledge the root causes that can initiate triggers, and explore methods for managing them effectively to help you in both your professional and personal life.",
    outcomes: [
      "Define emotional triggers and their impact on relationships.",
      "Recognize your emotional triggers and understand their repercussions in your life.",
      "Determine and utilize methods to manage your triggers effectively.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Practices Self-Awareness",
      "Pursues Self-Development",
    ],
    sessions: [
      {
        title: "Exploration of Emotional Triggers",
        body: "In this module, participants will explore emotional triggers and their importance, identify the Moment of Truth concept, and understand the typical reactions to conflicts that we experience.",
      },
      {
        title: "Trigger Thought Process",
        body: "In this module, we will define the Trigger Thought Process and explore what our personal triggers are and how they impact our lives. We will also identify ways we can choose to react positively or negatively to triggers.",
      },
      {
        title: "Managing our Triggers",
        body: "In this module, participants will identify how they can retrain their brains around their triggers. We will identify and practice techniques that will help shift our emotional state when we start to become triggered, including the use of the ART model.",
      },
    ],
  },
  "equity-diversity-and-inclusion-in-the-workplace": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Equity-Diversity-and-Inclusion-in-the-Workplace-.jpg",
    whoFor:
      "This course is appropriate for individuals at all levels of an organization who wish to better understand the need for Equity, Diversity, and Inclusion in the workplace and want to be part of making EDI a reality in their organization.",
    overview:
      "Equity, diversity, and inclusion (EDI) are at the forefront of the world's social agenda. There is a critical need to clearly define EDI and engage in meaningful, respectful discourse about its value to the workplace. Moreover, there is an evolving language of diversity that requires awareness, understanding, and skill in the same way as other competencies. This course serves as a call to conscientiousness. It provides learners with the knowledge, skills, and tools necessary to gain a comprehensive understanding of EDI. Additionally, it enhances the learner's ability to speak the language of diversity. Through interactive exercises and real-world scenarios, learners embark on a journey of enlightenment, self-discovery, and self-reflection.",
    outcomes: [
      "Define equity, diversity and inclusion.",
      "Recognize and manage bias.",
      "Understand and speak the language of diversity.",
      "Apply concepts learned in class, on the job and in the community.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Inspires",
      "Leverages Diversity",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Equity, Diversity and Inclusion (EDI)",
        body: "In this session, we will define Equity, Diversity and Inclusion (EDI) and recognize its value and benefits. We will also explore the value and benefits of a multicultural workplace.",
      },
      {
        title: "Understanding Bias",
        body: "In this session, we will explore the concept of bias, learn the types of bias, and understand how bias influences our thoughts and behaviors.",
      },
      {
        title: "The Language of Equity, Diversity and Inclusion",
        body: "In this session, we will identify the history behind commonly used divisive language, examine how conflict arises from negative language and behaviors, and determine how to build bridges to meaningful, healthy relationships through positive conversations and behaviors.",
      },
      {
        title: "Be Part of the Solution",
        body: "In this session, we will define how to cultivate an EDI mindset among ourselves and our coworkers. We will also explore how to develop an inward mindset and how to speak up and speak out effectively.",
      },
    ],
  },
  "ethics-in-the-workplace": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/07/Ethics_in_the_Workplace.webp",
    whoFor:
      "This course is ideal for managers or experienced leaders in human resource management, policy making, or corporate training roles.",
    overview:
      "Working in an environment with unethical behavior can have negative consequences on organizational trust, team building, communication, and morale. Sometimes individual character changes are in order, but other times a company needs to systematically change its culture. In this course, participants will discover how to pinpoint unethical behavior, create a plan for an effective ethics policy, and deal with unethical co-workers. This will lead to building organizational trust, greater integrity when approaching problems, increased respect among workers, and it will establish standards of ethical behavior.",
    outcomes: [
      "Define workplace ethics and common myths about business ethics.",
      "Recognize the decision-making process that helps create ethical best practices.",
      "Identify and resolve typical ethical dilemmas that will help you implement guidelines and policies for your organization.",
    ],
    behaviors: [
      "Builds Trust",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Workplace Ethics",
        body: "In this module, we will define ethics, its importance and how it impacts businesses today. We will explore the ideal ethical workplace, the benefits of an ethical environment and identify some myths about business ethics.",
      },
      {
        title: "Dealing with Ethics in Real Life Situations",
        body: "In this module, we will examine the decision-making processes, the best ways to deal with real-life situations when unethical behavior is happening, and methods to keep yourself consistently ethical.",
      },
      {
        title: "Ethics Policy",
        body: "In this module, we will define and explore an ethics policy and understand why it should be implemented within an organization. Participants will also dive into some real-life ethical dilemmas and practice effective methods to address them.",
      },
    ],
  },
  "financial-intelligence": {
    detailed: true,
    level: "Intermediate",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/image_9.webp",
    whoFor:
      "This course is for leaders, managers and business owners who want to learn the practical skill of financial analysis using various financial instruments.",
    overview:
      "In today’s corporate world, a basic working knowledge of financial statements and calculations is necessary to make sound decisions about all aspects of a business. In this course, you learn to quantify your department’s contribution to the organization—and your own. We will not only explore the different statements and terms needed to understand the financial health of your organization, but we will also run simulations that will help you put these learned skills into practice.",
    outcomes: [
      "Identify the purpose and benefits of budgeting, planning, and income statements.",
      "Apply financial intelligence concepts about income statements, balance sheets, and cash flow statements.",
      "Recognize the purpose and benefits of balance sheet and cash flow statements.",
      "Interpret an organization’s 10-K, ROI and ROA.",
    ],
    behaviors: ["Communicates", "Diagnoses Needs", "Pursues Self-Development"],
    sessions: [
      {
        title: "Financial Intelligence Foundations",
        body: "As leaders, it is important to understand the basic concepts of financial intelligence. We will explore the use of an income statement, a balance sheet, and a cash flow statement for non-financial leaders.",
      },
      {
        title: "The Balance Sheet",
        body: "In this session, we will establish a basic balance sheet as well as identify several key terms that are essential for financial intelligence.",
      },
      {
        title: "The Income Statement",
        body: "During this session, we will track the organization’s sales and complete an income statement. We will also establish working definitions for retained earnings, accounts payable, accounts receivable, and labor expenses.",
      },
      {
        title: "The Cash Flow Statement",
        body: "In this session, we will identify the purpose and uses of a cash flow statement.",
      },
      {
        title: "10-K, ROI, and ROA",
        body: "This session is designed to bring everything we have learned so far in this course together. We will explore how to read an organization’s 10-K (quarterly statements) and why tracking ROI (return on investment) and ROA (return on assets) is important.",
      },
    ],
  },
  "finding-your-purpose": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Finding-Your-Purpose-1-scaled.jpg",
    whoFor:
      "This course is ideal for any business professional who wants to find purpose in their life and is ready to reach their fullest potential.",
    overview:
      "Finding and recognizing your purpose gives direction to your life and helps you to focus on the things that are important to you. Your purpose is comprised of your values (who you are), vision (your definition of what a good job looks like for you) and mission (how you accomplish your vision). In this course, we will explore the process of finding our purpose in life and identifying our personal values. Participants will learn to employ a four-step approach to write their vision statement, enabling them to pursue and accomplish their mission.",
    outcomes: [
      "Understand the importance of finding your purpose and recognize your personal values.",
      "Determine your vision statement based on your values.",
      "Relate your vision statement to your mission so you can put your values, vision, and mission into action.",
    ],
    behaviors: [
      "Champions Change",
      "Demonstrates Self-Management",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Finding Your Purpose",
        body: "In this module, participants will define their personal values, explore why finding their purpose is so important, and learn how personal values represent them.",
      },
      {
        title: "Personal Vision Statement",
        body: "Your vision statement is a reflection of the values you have and how you want to show those values to others. In this module, we will explore vision statements and the four steps that need to be followed to write your own personal vision statement.",
      },
      {
        title: "Your Mission",
        body: "Your mission describes how you want to accomplish your vision. In this module, we will explore how to proceed with your vision and put it into action. We will also examine how to adjust your mission as needed, while still aligning to your vision statement as you progress in life.",
      },
    ],
  },
  "fundamentals-of-strategic-planning": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Fundamentals-of-Strategic-Planning-1-scaled.jpg",
    whoFor:
      "This course is for anyone who actively contributes to the long-term planning of the organization and desires to be proactive, rather than reactive, to situations as they arise.",
    overview:
      "Strategic planning provides organizations with a sense of direction and outlines measurable goals. It also allows organizations to foresee their future and prepare accordingly. With a strong strategic plan, organizations can be proactive rather than merely reacting to situations as they arise. Being proactive allows organizations to keep up with the ever-changing trends in the market and always stay one step ahead of the competition. This course examines the strategic planning process, the importance of vision statements, core values, and the importance of SWOT analysis. In this course, you will discover how to create effective strategic planning for your organization.",
    outcomes: [
      "Identify the strategic planning concepts and the importance of vision statements.",
      "Examine the importance of core values and SWOT analysis.",
      "Determine how to create an effective strategic plan for your organization.",
    ],
    behaviors: ["Inspires", "Sets Goals", "Solves Problems"],
    sessions: [
      {
        title: "Strategic Planning Concepts",
        body: "This module will focus on defining strategic planning and its importance. We will identify an organization’s vision and the negative impact of not creating a vision. In this module, we will explore the basic steps in strategic planning and identify the importance of choosing a good leader for the strategic planning team.",
      },
      {
        title: "SWOT Analysis",
        body: "This module explores core values and how they guide you. We will examine the importance of a SWOT analysis in the strategic planning process. We will also analyze the process for validating the organization’s strengths and weaknesses.",
      },
      {
        title: "Creating a Strategic Plan",
        body: "In this module, we will identify the importance of writing your goals, determine how to use SMART objectives to achieve your goals, and recognize the importance of monitoring and evaluating. Finally, we will explore the importance of reward and celebration in an organization.",
      },
    ],
  },
  "on-the-spot-development-conversations": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2020/10/Giving-Feedback-through-On-the-Spot-Development-Conversations-Cover-Image1.png",
    whoFor:
      "This course is for any leader who is responsible for achieving results through others and wants to improve their team’s development.",
    overview:
      "Leaders are responsible for developing others. On-the-spot development conversations are a necessary tool for the personal and professional development of your team members. In this course, you will understand the difference between a development conversation and a performance conversation. You will also learn how to conduct an effective development conversation and avoid the pitfalls.",
    outcomes: [
      "Differentiate between development and performance conversations.",
      "Define methods to conduct effective development conversations.",
      "Identify common pitfalls and the importance of documentation.",
    ],
    behaviors: ["Builds Trust", "Communicates", "Inspires", "Solves Problems"],
    sessions: [
      {
        title: "Two Types of Conversations",
        body: "In this module, we will define the purpose and differentiate between development and performance conversations. Participants will identify how development conversations can sometimes turn into a performance conversation, and the effects of it to both the employee and the leader.",
      },
      {
        title: "Having Development Conversations",
        body: "In this module, we will discuss how to have a development conversation by defining and challenging your motives, using the conversation flow method and the Losada Ratio principles.",
      },
      {
        title: "Avoiding Common Pitfalls",
        body: "In this module, we will identify some common pitfalls that lead to poorly executed development conversations. Participants will also recognize the role and importance of documentation.",
      },
    ],
  },
  "how-to-make-yourself-indispensable": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/How-To-Make-Yourself-Indispensable-.jpg",
    whoFor:
      "This course is ideal for any business professional who wants to bring their best to all endeavors by seeking to be a lifelong learner who strives for continual self-improvement.",
    overview:
      "You are ultimately responsible for who you are, what you do, and how you show up. You must decide you want to make a difference to your company, your team, your business–even your family and friends. Being indispensable requires dedication and a healthy work ethic. There are several universal steps you can take to be an invaluable team member. In this course, we will explore the importance of perspective, how to maintain relationships, and the importance of taking ownership to be indispensable to your organization.",
    outcomes: [
      "Recognize the importance of having the right perspective.",
      "Determine methods to maintain the right relationships in the workplace.",
      "Define the right actions to take to become indispensable.",
      "Identify the impact of taking ownership.",
    ],
    behaviors: ["Communicates", "Innovates", "Takes Initiative"],
    sessions: [
      {
        title: "Having the Right Perspective",
        body: "In this module, we will differentiate between indispensable and irreplaceable. We will examine how to bring solutions instead of problems, how to offer something unique, and how to be flexible. We will also explore strategies for dealing with change.",
      },
      {
        title: "Maintaining the Right Relationships",
        body: "In this module, we will examine how the importance of honesty and openness, good communication, and the ability to maintain a calm composure all nurture our relationships. We will dive into each skill and address how it relates to healthy relationships within the workplace.",
      },
      {
        title: "Taking the Right Steps",
        body: "In this module, we will examine how to perform triage (most critical issues, timeframes, and action plan), determine how to expand your sphere of influence, and evaluate the best use of power messages.",
      },
      {
        title: "Taking Ownership",
        body: "In this module, we will explore the need for taking initiative. We will focus on key metrics and results achieved and the methods necessary to effectively handle mistakes. Participants will learn to recognize the importance of a root cause analysis and identify how to act like a business owner.",
      },
    ],
  },
  "influence-without-authority": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Influence-without-Authority.jpg",
    whoFor:
      "This course is designed for leaders who wish to be more influential in their organization without positional power.",
    overview:
      "It is commonly assumed that only a person with power and authority can influence. However, influencing goes beyond positions of power and encompasses more subtle skills like empathy and active listening. In this course, you will learn the skills, strategies, and behavior needed to influence others without direct power. You will explore how to demonstrate authenticity and integrity through your interactions with others and how to have a strong sense of self-awareness that lays the groundwork for mastering the art of influence—leading to improved working relationships and increased opportunities to see your ideas grow.",
    outcomes: [
      "Define influence and the skills needed to influence using the CIA model.",
      "Identify influence building behaviors.",
      "Recognize influence destroying behaviors.",
    ],
    behaviors: [
      "Builds Trust",
      "Communicates",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Inspires",
      "Matches Style to Needs",
    ],
    sessions: [
      {
        title: "Defining Influence",
        body: "In this module, we will define influence, why it is important, and the impact it has on others. Participants will also examine the CIA influencing model and methods to develop the ability to identify nonverbal behaviors.",
      },
      {
        title: "Essential Influence Building Behaviors",
        body: "In this module, we will examine influence building behaviors in detail, including conflict resolution, empathy, being proactive, using neutral language, and assessing before you evaluate.",
      },
      {
        title: "Influence Destroying Behaviors",
        body: "This module focuses on the most common and destructive behaviors that affect our influencing ability, including sarcasm, value-based attacks, using “you” statements, and continuously asking, “Why?”. Participants will analyze and understand these behaviors and learn how to avoid them.",
      },
    ],
  },
  "introduction-to-servant-leadership": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Introduction-to-Servant-Leadership-1-1-scaled.jpg",
    whoFor:
      "We are all leaders – by choice or default. Although this course provides concepts, tools, and resources for all leaders, extra emphasis and application are directed toward:\n• Leaders who currently have a team of direct reports.\n• Those who want to learn more about leadership in preparation for having a team.",
    overview:
      "Servant Leadership is an extremely powerful leadership approach and it is the epitome of selflessness. While other leadership philosophies are fixated on organizational goals, the servant leader remains focused on employee needs to maximize individual development and potential. On the surface, this philosophy might appear to contradict organizational success. However, by giving employees what they need (such as accountability, nurturing, responsibility, coaching etc.) sustainable organizational success tends to follow. Servant Leadership can be summarized with the following: if you’re not serving others, then you are not leading.",
    outcomes: [
      "Define the fundamentals of servant leadership and the reasons people choose to follow servant leaders.",
      "Identify servant leadership concepts in self, employees and community.",
      "Identify the Four Leadership Principles and how servant leaders apply them successfully.",
      "Determine how servant leaders balance humble service with an action driven approach.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Inspires",
      "Leverages Diversity",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Introduction to Servant Leadership",
        body: "In this module, participants are introduced to the foundational ideas of servant leadership. This includes leading consciously, recognizing that people choose to follow leaders they respect, and knowledge that leadership must begin within oneself and then extends to others.",
      },
      {
        title: "Why Being a Servant Leader Matters",
        body: "Participants in this module will continue to examine the values and goals of the servant leader through the priorities, principles and practices of a successful servant leader.",
      },
      {
        title: "How Do I Become a Servant Leader?",
        body: "In this module, participants will explore how to recognize and draw upon the unique gifts and talents of their employees and determine how they can develop and leverage those talents to benefit the individual and the organization. In addition, participants will explore the Four Leadership Principles and how servant leaders apply them for success.",
      },
      {
        title: "Servant Leadership Steps and Techniques",
        body: "This module incorporates the ideas presented in the previous modules and provides practical tips for applying the concepts into everyday activities. The module ends with a discussion of the foundational idea of the humble, service-oriented, yet action-driven leader, both of whom can co-exist and complement each other.",
      },
    ],
  },
  "leading-leaders": {
    detailed: true,
    level: "Experienced",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Leading-Leaders.jpg",
    whoFor:
      "This course is specifically designed for senior executives and experienced leaders who need to strengthen and advance their leadership skills to meet the challenges of today’s corporate world. This course is also for any leader who is expecting to be in a higher managerial role.",
    overview:
      "Highly talented organizations have a greater concentration of talented people whose levels of education, skill, ability, and influence are substantially above average. It is challenging to lead top talent in the modern corporate world. Therefore, senior leaders need to think more strategically, communicate more persuasively, and develop the leadership skills of those around them. One who possesses the ability to lead other leaders will have the opportunity to obtain a priority role within their organization. This course offers strategies to reinforce your leadership skills and build your team of leaders to achieve organizational goals. You will become a better coach to your high potential leaders and develop the ability to convey your vision to them, while preparing yourself for more senior responsibilities.",
    outcomes: [
      "Differentiate between leading leaders and leading others, and identify how to set the vision effectively.",
      "Recognize and understand right voice and cause-and-effect thinking.",
      "Determine the right development needed for your leaders.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Inspires",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Leading Leaders: Setting the Vision",
        body: "This module will focus on defining what it means to lead leaders, how it is different from leading others, and what the key roles are of leading leaders. We will explore how leaders exert their leadership by setting the organizational strategic vision effectively and how the goals of top-level leaders are connected to next-level-down leaders.",
      },
      {
        title: "Identifying Different Relationships",
        body: "This module will focus on how communication changes when communicating with leaders. We will explore the importance of reinforcing cause-and-effect thinking, how to communicate your vision to leaders, and how to manage the amount of influence you have in your organization.",
      },
      {
        title: "Development is Different",
        body: "When you lead leaders, the development changes from technical to leadership expertise. In this session, we will identify how to help your leaders communicate effectively, set the vision, and lead their team effectively.",
      },
    ],
  },
  // TODO: confirm with client — PDF catalog says 4 hours / 4 sessions, old website said 3 weeks. Using 4 to match session count.
  "leading-with-an-inclusive-mindset": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Leading-with-an-Inclusive-Mindset-1-scaled.jpg",
    whoFor:
      "This course is intended for members of leadership at all levels of the organization who want to create a more inclusive culture at work.",
    overview:
      "There is a crucial wide-spread movement happening within organizations to reduce workplace bias and become more inclusive; however, the demographic makeup of the leadership roles within organizations remains unchanged. Organizations with diverse and inclusive practices have better operating performance and profitability than those without an inclusive mindset. Creating an inclusive mindset and culture begins with leadership. This course prepares organizational leaders to improve diversity and inclusion practices through accountability, using power and influence to drive change and model the empathy needed during the process.",
    outcomes: [
      "Define what it means to have an inclusive mindset.",
      "Identify the knowledge, skills, and tools needed to develop an inclusive mindset.",
      "Develop strategies to be accountable for creating and fostering inclusiveness.",
      "Utilize tools and techniques to address bias.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Inspires",
      "Leverages Diversity",
      "Practices Self-Awareness",
      "Sets Goals",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "What Does It Mean to Have an Inclusive Mindset?",
        body: "In this session, we will explore what it means to lead with an inclusive mindset and why it is important to the organization and the company.",
      },
      {
        title: "Developing an Inclusive Mindset",
        body: "In this session, we will identify the knowledge, skills, and tools necessary to develop an inclusive mindset. Participants will explore their current mindset through a journey of self-reflection and self-discovery.",
      },
      {
        title: "Inclusion Accountability",
        body: "In this session, inclusion accountability and its importance will be defined and explored. Participants will apply self-awareness and self-regulation skills to develop strategies for holding themselves accountable for creating and fostering a workplace environment of inclusiveness.",
      },
      {
        title: "Influence and Empathy",
        body: "Influence and empathy are two key skills for developing an inclusive mindset. In this session, we will recognize how to use emotional intelligence and empathy as skills to influence inclusiveness in the organization. We will also identify tools and techniques to build and enhance emotional intelligence and empathy skills.",
      },
    ],
  },
  "making-better-decisions-through-critical-thinking": {
    detailed: true,
    level: "Foundational",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Making-Better-Decisions-through-Critical-Thinking-1-1.jpg",
    whoFor:
      "This course is for business leaders who want to develop their thinking processes to achieve better results. This course aims to help develop and improve these critical thinking skills.",
    overview:
      "This course presents basic concepts of critical thinking and how it benefits you in your professional as well as personal life. We will be discussing the role of curiosity and the concept of Johari Window. We will also learn the Six-step Systematic Process of Critical Thinking and apply the relevant questions to better understand the process. Finally, we will be looking at the value of data in making better decisions.",
    outcomes: [
      "Define critical thinking and its benefits.",
      "Discuss the role of curiosity and the Johari Window.",
      "List the Six-step systematic process of critical thinking (two modules).",
      "Explain the value of data in the critical thinking process.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Diagnoses Needs",
      "Inspires",
      "Practices Self-Awareness",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Introduction to Critical Thinking",
        body: "In this module we will define critical thinking. We also talk about the benefits of critical thinking in our professional and personal life.",
      },
      {
        title: "Curiosity and the Johari Window",
        body: "This module looks at the importance of curiosity and how it affects our thinking. We will also introduce the Johari Window.",
      },
      {
        title: "The Systematic Six-Step Process of Critical Thinking – Part 1",
        body: "In this module, we introduce the Six-step Systematic Process for Critical Thinking. This module will define the first three steps: Interpretation, Analysis, and Inference.",
      },
      {
        title: "The Systematic Six-Step Process of Critical Thinking – Part 2",
        body: "In this module, we will define the second three steps of the Systematic Process for Critical Thinking: Evaluation, Explanation and Self-Regulation.",
      },
      {
        title: "The Value of Data",
        body: "This module explains how data plays a pivotal role in the process of decision making and how it impacts your critical thinking. With the right data, you have the ability to develop the right questions that lead to better decisions.",
      },
    ],
  },
  "manage-your-time-master-your-life": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Manage-Your-Time-Master-Your-Life.jpg",
    whoFor:
      "This course is for anyone who finds themselves struggling to find enough time in their day, often feeling stressed and exhausted. It is for those who wish to develop productive habits that will help them make the most of their time each day.",
    overview:
      "You will develop the required skills in time management to achieve effective results. This course covers four time management principles, how to prioritize tasks, the best communication methods, and the tools required to be effective in your work. It will also cover how to plan your meetings to be an effective leader in your work environment. This course will help improve your efficiency by aligning your priorities.",
    outcomes: [
      "Define time management and its importance.",
      "Identify your purpose and role in your organization.",
      "Identify the core four elements and a process to effectively manage them.",
      "Recognize the importance of effective communication in time management.",
      "Indicate which area of your life needs balance in order to manage your time better.",
    ],
    behaviors: ["Communicates", "Focuses on Results through People", "Inspires", "Solves Problems"],
    sessions: [
      {
        title: "Time Management Introduction",
        body: "This module explains what time management is and its importance, helping you to develop the right mindset. We will identify your purpose and role within your organization by completing a self-reflection activity. This will help you get clarity on your purpose and role.",
      },
      {
        title: "Time Management Principles & Evaluating",
        body: "This module looks at the four important core time management principles and how they affect your daily routine. Use the time matrix to discuss the difference between time management and priority management.",
      },
      {
        title: "Ideal Communication Methods",
        body: "In this module, we will discuss the main components to effective communication and three key behaviors.",
      },
      {
        title: "Balance Your Life",
        body: "This module discusses the four areas of life that can get out of balance when time management is not improved and can lead to poor performance.",
      },
    ],
  },
  "managing-emotions": {
    detailed: true,
    level: "Intermediate",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Managing-Emotions.jpg",
    whoFor:
      "This course is ideal for leaders who not only want to effectively manage their own emotions but also want to learn techniques that will help them handle the emotions of others.",
    overview:
      "In a typical workday, you may encounter situations that spark a variety of emotions, such as disagreements over decisions, frustration that someone is not taking you seriously, disappointment over not getting a promotion, or maybe complete dissatisfaction with your job. It is important to know when and how to express your emotions, and when to keep them in check. Outbursts, passive-aggressive behavior, or detachment are never responsible ways to handle emotions at work. This course is designed to give you the tools you need to recognize your emotional triggers, control your emotional and physical responses, and handle your response to others’ emotions. As a result, you will be able to communicate more effectively with supervisors and coworkers, channel negative energy into more positive and productive activities, and increase your emotional intelligence.",
    outcomes: [
      "Recognize your emotional triggers and the ART model to help shift your emotional state.",
      "Determine strategies that replace emotional outbursts with productive confrontations.",
      "Determine techniques to manage your response to the emotional outbursts and needs of others.",
    ],
    behaviors: [
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Matches Style to Needs",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "The What, Where, and Why of Emotions",
        body: "This module will examine what emotions are, where they originate, and why they are important. We will identify common workplace triggers and discover your personal triggers. Finally, we will recall and practice techniques to help you shift your emotional state when you begin to feel triggered, including the use of the ART model.",
      },
      {
        title: "Managing Emotions within Yourself",
        body: "This module explores strategies for controlling your physical responses when you are emotionally triggered. We will practice reframing emotional statements and determine how to have productive confrontations. Finally, we will identify the four steps to problem solving.",
      },
      {
        title: "Managing Emotions in Others",
        body: "This module will address how to manage the emotional needs of your highly energetic and excited team members. We will explore how to adapt your response to the emotional outbursts of others and learn how to manage their emotional needs.",
      },
    ],
  },
  "managing-stress": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Managing-Stress-1-scaled.jpg",
    whoFor:
      "This course is ideal for leaders who manage in stressful environments and want to help their team handle stress more effectively. This course will also provide any business professional who is feeling burned out, deeply tired, and demotivated the insight they need to manage stress.",
    overview:
      "Stress management helps you break the hold stress has on your life so you can be happier, healthier, and more productive. The goal of managing stress is a balanced work and personal life. In this course, we will define stress and its effects, discuss the types of stress we encounter, examine the Yerkes-Dodson Law of Performance, and explore ways to manage stress effectively.",
    outcomes: [
      "Understand stress, types of stress, and the",
      "Yerkes-Dodson Law of Performance.",
      "Identify the effects, signs, and symptoms of stress and learn effective ways to manage it.",
      "Explore the six-step formula for managing stress and the Circle of Influence.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Pursues Self-Development",
      "Practices Self-Awareness",
    ],
    sessions: [
      {
        title: "Introduction to Managing Stress",
        body: "In this module, we will explore stress and how it affects us personally and professionally. We will also define the different types of stress and examine the Yerkes-Dodson Law of Performance.",
      },
      {
        title: "Effects of Stress",
        body: "In this module, we will examine the effects stress has on us mentally, emotionally, and physically, identify the signs and symptoms of stress, and explore the effects stress can have on performance and motivation.",
      },
      {
        title: "Managing Your Stress",
        body: "In this module, we will determine best practices to manage stress, examine the six-step formula for coping with stress, and identify the Circle of Influence concept.",
      },
    ],
  },
  "managing-up": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Managing-Up.jpg",
    whoFor:
      "This course is designed for any business professional who wants to receive merit and recognition from their supervisor for their hard work and ideas. Those who seek a better relationship with their supervisor will also benefit from taking this course.",
    overview:
      "The reality of today’s workplace is that individual success depends not only on how well you do your job, but how well your supervisor does their job. A supervisor’s role should not come from a command and control mindset, but rather a mindset of collaboration. When our supervisors are unable to meet deadlines, accomplish goals, and fulfill expectations, those unmet obligations inevitably become yours, and your relationship can suffer greatly. This course presents the principles of managing up that help you to be more in tune with your supervisor’s needs and help you to support their daily obligations, which in turn will benefit you. Your work life will run more smoothly, you will encounter less conflict, and you will make a positive impression on your supervisor.",
    outcomes: [
      "Identify the principles of managing up and determine expectations.",
      "Establish a clear system of organization and discover methods that eliminate preventable problems.",
      "Identify best practices for handling difficult and frustrating situations and determine positive behaviors that make you an invaluable employee.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Pursues Self-Development",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Managing Up: Expectations",
        body: "In this module, we will define the concept and principles of managing up. We will also identify the best practices for meeting with your supervisor regularly and recognize what is expected of you so that you are both on the same page.",
      },
      {
        title: "Managing Your Supervisor",
        body: "In this module, you will learn to identify your supervisor’s priorities, goals, and pressures. Next, you will define a clear system of organization of information and examine how to eliminate preventable problems in your everyday work and interactions.",
      },
      {
        title: "Managing Yourself",
        body: "In this module, we will recognize the behaviors that frustrate your supervisor and learn ways to avoid them. We will also examine the behaviors that reflect positively on you and identify best practices for handling situations that frustrate you. Finally, we will also explore ways to engage in polished self-promotion.",
      },
    ],
  },
  "masterfully-managing-meetings": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Masterfully-Managing-Meetings.jpg",
    whoFor:
      "This course is ideal for leaders and managers who want to run more effective meetings and desire to learn the importance of leader behaviors during meetings.",
    overview:
      "Do you know why meetings have a bad reputation? It is estimated that Fortune 500 companies waste $75 million per year in unproductive meeting time. Poorly run meetings take too long, involve too many people, and don’t seem to result in any concrete action. Despite this, meetings are necessary and can be extremely effective if run properly. Meetings that are planned and facilitated well give participants a sense of accomplishment and the knowledge that their time was well spent. This course will give you the skills and strategies needed to manage meetings efficiently and effectively.",
    outcomes: [
      "Recognize when a meeting should be held and identify its purpose.",
      "Identify and implement the tools needed to manage effective meetings.",
      "Examine effective facilitation skills that ensure a meeting ends with actionable items.",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Focuses on Results through People",
    ],
    sessions: [
      {
        title: "Meeting Basics",
        body: "As many of us have experienced, not every meeting needs to be a meeting. In this session, we will differentiate between when we should have meetings and when we should interact in a different way, such as sending an email, or having an open house. We will identify the purpose of meetings and the importance of establishing an agenda.",
      },
      {
        title: "Managing Effective Meetings",
        body: "This session explores strategies for managing meetings with purpose. We will get an insight into the tools that assist in meeting management such as OneNote and Microsoft Teams. We will also practice some best practices and strategies that make meetings more efficient and effective.",
      },
      {
        title: "Facilitating Meetings",
        body: "In this session, we will explore all aspects of effective meeting facilitation and discover how to solicit the best ideas from those who attend. Some methods to be discussed include brainstorming, online polling, and group discussions. Finally, we will learn how to ensure that every meeting ends with actionable items.",
      },
    ],
  },
  "maximizing-strengths": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Maximizing-Strengths.avif",
    whoFor:
      "This course would benefit anyone who is seeking to optimize their impact as a leader though a better awareness and utilization of their strengths in the professional world.",
    overview:
      "Self-awareness is essential to success in personal and professional life. Leaders who know their strengths can learn to utilize them to be effective and productive at work. In this module, participants will learn how to identify their personal strengths and examine how to use and develop those skills to become better leaders.",
    outcomes: [
      "Understand the strengths-based approach and the importance of knowing your strengths.",
      "Identify personal strengths.",
      "Learn how to use personal strengths as an asset in the workplace.",
    ],
    behaviors: ["Demonstrates Self-Management", "Pursues Self-Development", "Solves Problems"],
    sessions: [
      {
        title: "Introduction to Strengths-Based Approach",
        body: "In this module, we will introduce the strengths-based approach, with emphasis on the “Via”, the “CAPP,” and the “Gallup” approaches. Participants will understand the benefits of each approach and learn how they are best utilized.",
      },
      {
        title: "Understanding Your Personal Strengths",
        body: "In this module, participants will identify and understand their personal strengths. We will also learn new methods to identify personal strengths at work such as listening to feedback, considering your passions, and paying attention to where you are most productive.",
      },
      {
        title: "Optimal Use of Your Strengths",
        body: "In this module, we will identify how to regulate strengths by addressing the overuse, underuse, and misuse of strengths. We will explore some methods to best utilize strengths, such as self-monitoring, working them into the daily routine, and searching out new opportunities to use strengths.",
      },
    ],
  },
  "mindfulness-as-a-differentiator": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Mindfulness-as-a-Differentiator.jpg",
    whoFor:
      "This course is designed for you to learn mindfulness strategies that will help you to be mindful in your relationships and in your workplace.",
    overview:
      "Have you ever noticed that some of your co-workers seem to go through work and life with ease, but you feel as though life is a constant struggle and things are not in your favor? The differentiator that they practice is mindfulness. Mindfulness is a mental state where your awareness is centered on the present moment. This course breaks down mindfulness in a comprehensive way by addressing the most important elements of mindfulness, one by one. The course offers some valuable mindful practices that will change your life and support your workplace environment, leading to the building of healthy relationships.",
    outcomes: [
      "Recognize what mindfulness is and understand its benefits.",
      "Identify mindful exercises.",
      "Utilize mindfulness in your relationships and in your workplace.",
    ],
    behaviors: [
      "Champions Change",
      "Demonstrates Self-Management",
      "Practices Self-Awareness",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "What is Mindfulness?",
        body: "In this session, we will define mindfulness and learn how to practice it. We will also explore why mindfulness in the workplace is so important and what benefits employees receive when they use it.",
      },
      {
        title: "Practicing Mindfulness Exercises",
        body: "In this session, we will identify the exercises that will help you to be mindful. You will discover different practices for mindfulness such as meditation and physical practices. We will also explore how mindfulness-based strategies are built on attention to the present.",
      },
      {
        title: "Being Mindful in Your Relationships and Workplace",
        body: "This session will establish the basis for developing mindfulness in your relationship and in your workplace. We will examine how mindfulness in the workplace helps you to live in the moment and we will explore practical steps to add mindfulness to your relationships.",
      },
    ],
  },
  "motivation-models-carrot-or-stick": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Motivation-Models-Carrot-or-Stick_-1.jpg",
    whoFor:
      "This course is valuable for any leader who is looking to keep their team motivated in a new and meaningful way and wants to step away from the traditional carrot or stick model.",
    overview:
      "Keeping employees motivated and challenged can be a complex task. Over the years, many leaders have relied on the carrot and stick motivation model (i.e. carrot is a reward for good behavior and stick is a consequence for poor behavior.) However, when your goal as a leader becomes compliance, chances are the leader will be the only one motivated in the team. This makes it critical for leaders to understand the needs of their employees and find other methods to motivate them. This course will teach participants how to tailor their leadership and communication styles to suit the needs of the team. This will create a shared vision for the organization, build group identity, create a culture of ownership, and establish a more collaborative, inspiring work environment.",
    outcomes: [
      "Define the intrinsic and extrinsic motivation models and stages of motivation.",
      "Determine the goals and reward system that is necessary to enhance motivation.",
      "Identify major factors that affect motivation.",
    ],
    behaviors: [
      "Builds Trust",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Matches Style to Needs",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Motivation Models",
        body: "Leaders should consider doing certain motivation models together that will enhance employee motivation. In this module, participants will explore the intrinsic and extrinsic models as well as identify the four stages of motivation, including clarity, desire, ability, and reinforcement.",
      },
      {
        title: "Extrinsic Motivation Model",
        body: "This module will examine how the social structure and environment around us affect motivation. Participants will also explore the four Ps of the extrinsic motivation model and explore how to set goals and a reward system using the Losada principle.",
      },
      {
        title: "Intrinsic Motivation Model",
        body: "This module will establish the basis for intrinsic motivation factors such as vision, mission, goals, and roles. Participants will explore how these factors align with personal values, and will examine what builds internal motivation in their teams.",
      },
    ],
  },
  "navigating-organizational-politics": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2020/10/Navigating-Organizational-Politics-1.png",
    whoFor:
      "This course is ideal for leaders at all levels or anyone who desires to have a better understanding of the strategies needed to successfully navigate organizational politics and overcome challenging situations within the workplace.",
    overview:
      "Organizational politics involves the use of influential tactics that improve personal or organizational interests. A leader with good political skills manages to gain more personal power and can handle stress and job demands better, therefore having a greater impact on organizational goals. The focus of this course is on building a strong understanding of organizational politics, addressing challenges, and discovering ways to ethically approach workplace politics.",
    outcomes: [
      "Define the major aspects of organizational politics and their benefits and challenges to the company.",
      "Examine the role of people in organizational politics and determine ways to strengthen relationships and influence others through the use of positive behaviors.",
      "Determine the role that soft and hard power play in the organizational life, and learn strategies to leverage it effectively to build relationships, meet goals, and create momentum.",
      "Recognize the impact that building a positive and influential culture has on an organizations’ results.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Focuses on Results through People",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Defining Organizational Politics",
        body: "In this module, we will explore a broad overview of organizational politics and discuss the benefits and challenges associated with it. We will also identify three aspects of organizational politics: People, Power and Potential. We will learn how each of these aspects plays an important role in navigating the political landscape.",
      },
      {
        title: "The People Aspect of Organizational Politics",
        body: "In this module, we will explore how the People aspect (specifically, leader behaviors) impacts the political landscape and can be leveraged to positively influence the company or team culture.",
      },
      {
        title: "The Source of Power Aspect",
        body: "Successful leaders are using a more integrative and participatory style with a greater emphasis on the soft power of attraction, rather than the hard power of command. However, the two are often used interchangeably to influence others in positive and negative ways. In this session, we will examine how soft and hard sources of power influence and impact the organizational system.",
      },
      {
        title: "The Potential Aspect",
        body: "In this module, we will explore the Potential Aspect and discover how understanding organizational politics can help leaders to be successful by creating a culture of connection, openness, and trust.",
      },
    ],
  },
  "negotiation-for-mutual-success": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Negotiation-for-Mutual-Success-1-scaled.jpg",
    whoFor:
      "This course is ideal for anyone who wants to be creative in negotiation skills, develop their listening skills, and use the art of persuasion for effective negotiation.",
    overview:
      "As a leader, you must effectively negotiate in a wide range of business contexts including deal-making, employment discussions, corporate team building, labor/management talks, contracts, handling disputes, employee compensation, business acquisition, and the fulfillment of the contractual obligation. Effective negotiations lead to the mutual success for both the team and the organization. This course will equip you to excel in negotiation skills and active listening, while understanding the role of persuasion in all effective negotiations.",
    outcomes: [
      "Identify ways to prepare yourself for various types of negotiations.",
      "Explore the need for, and develop, your listening skills.",
      "Develop the art of persuasion.",
    ],
    behaviors: ["Communicates", "Demonstrates Self-Management", "Solves Problems"],
    sessions: [
      {
        title: "Preparing Yourself",
        body: "In this module, participants will identify and explore the best moments for effective negotiations and analyze the real needs and expectations in a given situation. We will also recognize the importance of understanding the other side and define the importance of negotiation for mutual success as a team and organization.",
      },
      {
        title: "The Need for Listening Skills",
        body: "In this module, we will explore how to understand hidden verbal and nonverbal messages, examine the importance of monitoring what is heard and adjusting your approach, and understand the importance of maintaining composure in any negotiation.",
      },
      {
        title: "The Art of Persuasion",
        body: "In this module, we will explore the art of persuasion and its importance in negotiations. We will analyze mutual needs, discover how to convince the other side to commit first, and learn how to leverage appropriately. We will also identify how to respond ethically, but assertively. Finally, we will discuss when and how to end a negotiation.",
      },
    ],
  },
  "powerful-presentations": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Powerful-Presentations.jpg",
    whoFor:
      "This course is ideal for any business professional who wants to interact with the audience and communicate their message with clarity and engagement.",
    overview:
      "Effective presentation skills are important for both individual and organizational success. Presenting information clearly and effectively is a key skill to communicating your message. Presentations are required in almost every field. They are used to inform, educate, motivate, and persuade internal and external audiences. A well-crafted presentation also demonstrates professionalism and helps to reinforce an organization’s corporate image. In this course, we will explore designing and storyboarding and how they can help develop the content for a presentation. Participants will also learn how to deliver an effective and powerful presentation.",
    outcomes: [
      "Understand and utilize storyboarding and design for effective presentations.",
      "Define and employ presentation types and the 1-4-6 concept.",
      "Explore the golden rules of presentations.",
    ],
    behaviors: ["Communicates", "Inspires", "Pursues Self-Development"],
    sessions: [
      {
        title: "Designing and Storyboarding the Message",
        body: "In this module, we will define storyboarding, recognize its importance in business, and examine the science behind it. We will also explore the SCR Model and discover methods to assess the needs of the audience. We will then dive into the storyboarding process and learn how to put it to good use.",
      },
      {
        title: "Developing the Content",
        body: "This module explores the ideal presentation types as well as the 1-4-6 guideline for presentations. We will also examine how to use and balance images in your presentation.",
      },
      {
        title: "Delivering the Presentation",
        body: "In this module, we will learn to implement the golden rules of presentation, identify presentation tips that will help you deliver an excellent presentation, and explore how to read the positive and negative cues from the audience. Finally, we will explore how to set the objectives for your presentation, various questioning techniques, and the best methods for handling hostile questions.",
      },
    ],
  },
  "practical-project-management": {
    detailed: true,
    level: "Foundational",
    duration: "5 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Practical-Project-Management-1-scaled.jpg",
    whoFor:
      "This course is appropriate for employees and managers at any level and in any type of organization who are responsible for leading a project from inception to completion.",
    overview:
      "Today’s organizations rely on employees who can carry a project through from planning to completion, on time and on budget. This course will increase your knowledge of the essential components of managing a project.",
    outcomes: [
      "Identify the essential parts of the Project Life Cycle.",
      "Analyze the essential elements of a project plan.",
      "Define an effective launch event for a successful project.",
      "Identify best practices to monitor scope, timelines, and budgets, and how to keep a project on track.",
      "Recognize the importance of closing a project by evaluating its success.",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Diagnoses Needs",
      "Focuses on Results through People",
      "Innovates",
      "Matches Style to Needs",
      "Sets Goals",
      "Solves Problems",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Introduction to Project Management",
        body: "This module gives an overview of why practical project management matters and describes how the course will empower you as a professional.",
      },
      {
        title: "Plan the Project",
        body: "The module will explore each of the essential parts of a project lifecycle, including the identification of essential resources, milestones, project budgeting, and project risk analysis.",
      },
      {
        title: "Implement the Project",
        body: "This module explores how an effective launch meeting or team event is essential to implementing the project plan and steps to move the project forward.",
      },
      {
        title: "Monitor the Project",
        body: "This module explains how to work with your team to set up a system for monitoring and keeping a project on track.",
      },
      {
        title: "Wrap-Up and Evaluation of the Project",
        body: "The module describes how to bring a project to a close by wrapping things up and evaluating the success of the project.",
      },
    ],
  },
  "professional-pathways-building-a-vision-for-your-career": {
    detailed: true,
    level: "All Levels",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Professional-Pathways-–-Building-a-Vision-for-Your-Career.jpg",
    whoFor:
      "This course is designed for anyone who wants to develop their vision, define their values, and build a career aligned with their vision and values.",
    overview:
      "A personal vision statement is an excellent way to define your priorities in life. It clarifies your direction, guards you against imbalances, and filters your distractions. In this course, we will examine the need for creating a vision, develop the skills needed to create a vision statement, and set goals for achieving the vision.",
    outcomes: [
      "Understand the importance of creating a vision statement and identify your strengths, goals, and values.",
      "Identify your skills, the four personality styles, and the six steps needed to explore your life vision statement.",
      "Examine the five steps for developing your vision statement.",
      "Define the five methods for setting effective career development goals.",
    ],
    behaviors: [
      "Demonstrates Self-Management",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Sets Goals",
    ],
    sessions: [
      {
        title: "Creating your Vision",
        body: "In this module, we will explore the importance of creating a vision statement in our lives and what we gain by doing so. Participants will also identify their strengths, goals, and values.",
      },
      {
        title: "Skills and Personality Styles",
        body: "This module will help participants identify their skills and the professional roles they desire. We will also identify and explore the four personality styles and how each style is better suited for certain roles. Finally, we will examine the six steps to explore your life vision.",
      },
      {
        title: "Your Vision Statement",
        body: "In this module, we will explore the personal vision statements of some famous people and utilize the five-step plan to write our own vision statement.",
      },
      {
        title: "Developing Goals to Achieve Vision",
        body: "It’s important to create goals that will help you to achieve your vision. In this module, we will explore the five methods for setting effective goals and how they further you in your career. Finally, you will create your goals using the methods discussed in the module.",
      },
    ],
  },
  "servant-leadership-ii-manager-as-servant-leader": {
    detailed: true,
    level: "Advanced",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Servant-Leadership-II-Manager-as-Servant-Leader-1-scaled.jpg",
    whoFor:
      "While this topic will be valuable for anyone who wants to develop their Servant Leaderships skillset, this course is designed specifically for those who are supervising others. It is strongly recommended to take Introduction to Servant Leadership as a pre-requisite.",
    overview:
      "Servant Leadership is an extremely powerful leadership approach used by successful leaders world-wide, including those at Costco, FedEx, Starbucks and more. While other leadership philosophies are fixated on organizational goals, the servant leader remains focused on employee needs to maximize individual development and potential. These leaders know that providing employees what they need through Servant Leader principles leads to sustainable organizational success. As a manager, using the Servant Leadership approach means to be focused on developing the Servant Leadership philosophy and its associated behaviors in your everyday practice, so that you can provide the necessary support to your employees and others. This course will help you to discover and develop the necessary Principles, Priorities and Practices of Servant Leadership, creating a strong foundation of professional service.",
    outcomes: [
      "Review the fundamentals of servant leadership and understand why people choose to follow servant leaders.",
      "Discover the three Principles of Servant Leadership and learn how to apply them.",
      "Understand how utilizing the three Priorities of the Servant Leader will help you to build a strong, effective and trusting team.",
      "Learn and implement the three Practices of the Servant Leader in your daily interactions.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Collaborates and Promotes Teamwork",
      "Communicates",
      "Demonstrates Self-Management",
      "Focuses on Results through People",
      "Inspires",
      "Leverages Diversity",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "The Servant Leadership Philosophy",
        body: "In this session, participants will review foundational ideas of servant leadership and discuss how to begin building these ideas into an everyday, effective management practice.",
      },
      {
        title: "Three Leading Principles of Servant Leadership",
        body: "Participants in this session will continue to examine the values and goals of the servant leader through the three Leading Principles of Servant Leadership: Serve First, Persuasion, and Empowerment. We will discuss each and how they impact our effectiveness as a leader, as well as how to implement these principles in our actions.",
      },
      {
        title: "Three Leading Priorities of Servant Leadership",
        body: "Participants in this session will continue to examine the values and goals of the servant leader through the three Leading Priorities of Servant Leadership: Developing People, Building a Trusting Team, and Achieving Results. We will discuss each and how they impact our effectiveness as a leader, as well as how to implement these priorities in our actions.",
      },
      {
        title: "Three Leading Practices of Servant Leadership",
        body: "Participants in this session will continue to examine the values and goals of the servant leader through the Three Leading Practices of Servant Leadership: Listening, Delegating, and Connecting Followers to Mission. We will discuss each and how they impact our effectiveness as a leader, as well as how to implement these practices in our actions.",
      },
    ],
  },
  "skillful-collaboration": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Skillful-Collaboration.jpg",
    whoFor:
      "This course would benefit anyone who wants to collaborate with internal and external stakeholders and work towards achieving a common goal.",
    overview:
      "Successful collaboration involves far more than just working side-by-side. It’s a process that requires attitudes, skills, and practices that can be learned and strengthened, and mastering them will not only make your job easier, but it will also help you to become more successful. Collaboration increases accountability and increases motivation. In this course, you will recognize when and when not to use a collaboration approach, identify how to establish and communicate expectations to group members to ensure a successful collaboration, and recognize the inward attitudes and outward behaviors necessary to collaborate well.",
    outcomes: [
      "Identify the benefits and challenges of collaboration.",
      "Explore the skills required to collaborate with others effectively.",
      "Recognize ways collaboration can be destroyed.",
    ],
    behaviors: [
      "Collaborates and Promotes Teamwork",
      "Focuses on Results through People",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Benefits and Challenges of Collaboration",
        body: "In this module, we will explore the benefits and challenges of collaboration, when to collaborate, and who should be involved. We will also define how to establish expectations and collaborate with leaders. We will then determine how to create a community of practice and utilize the tools required for effective collaboration.",
      },
      {
        title: "Skills Necessary for Successful Collaboration",
        body: "In this module, we will learn how to develop the required attitude to be an effective collaborator and explore how to turn your conflicts into cooperation. We will determine how to get things done and explore the words to use and avoid for effective collaboration.",
      },
      {
        title: "Approaches that Destroy Collaboration",
        body: "In this module, we will define and explore how collaboration gets destroyed, why it happens, and methods we can use to manage it. Some key topics that will be explored in this module include protecting our turf, being indecisive, needing excessive help, and acting uninterested.",
      },
    ],
  },
  "soar-analysis": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/SOAR-Analysis.jpg",
    whoFor:
      "This course is ideal for any business professional who wants to enhance their development plans for themselves and/or their organization.",
    overview:
      "SOAR Analysis is a powerful tool to bring stakeholders together in recognizing the potential of the organization and creating a shared vision of the future. The SOAR Analysis is an action-oriented strategic planning method that helps organizations focus on their future vision and results. In this course, we will explore the SOAR Analysis model, how to use the SOAR Analysis for teams and within the entire organization, and the importance of SOAR Analysis for personal growth.",
    outcomes: [
      "Define SOAR Analysis and differentiate between SOAR and SWOT Analysis.",
      "Examine the importance of completing a SOAR Analysis for your organization using the 2x2 matrix and six-step process.",
      "Understand the value of completing a SOAR Analysis for yourself",
    ],
    behaviors: ["Innovates", "Sets Goals", "Takes Initiative"],
    sessions: [
      {
        title: "Introduction to SOAR Analysis",
        body: "In this module, we will define SOAR Analysis and its importance as well as how to perform one for teams, organizations and individuals. We will also differentiate between a SOAR Analysis and a SWOT Analysis.",
      },
      {
        title: "SOAR Analysis for Teams and Organizations",
        body: "In this module, we will explore the methods used in an effective SOAR Analysis for our team or organization. This includes using the 2x2 matrix and the six-step process.",
      },
      {
        title: "SOAR Analysis for Individuals",
        body: "In this module, we will examine how you can complete a SOAR analysis for yourself and explore its expected benefits. We will also discuss the use of the 2x2 matrix and six-step process as personal development tools.",
      },
    ],
  },
  "solid-business-writing": {
    detailed: true,
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    whoFor:
      "This course is ideal for any business professional who wants to clearly communicate messages across multiple channels, create documents that are easy to read, and get information out effectively.",
    overview:
      "All business activities are planned, implemented, and analyzed in some form of the written word. Organizations rely upon this written information, both internally and externally. Information must be written clearly and concisely to reduce miscommunication. In this course, you will determine how to craft writing that is easier to read, gets information out effectively, and reduces misunderstandings and mishaps. As a result, you will be able to create an image with words, represent your level of competence, avoid making embarrassing mistakes, and improve relationships with customers.",
    outcomes: [
      "Identify the need for business writing.",
      "Explore the best practices for electronic communication.",
      "Recognize the importance of framing your message.",
    ],
    sessions: [
      {
        title: "The Need for Solid Business Writing",
        body: "In this session, we will explore the need for effective writing skills and the ingredients of a well-written document. We will also recognize the importance of planning and analyzing your audience.",
      },
      {
        title: "Email Tips and Tricks",
        body: "In this session, we will examine all aspects of email etiquette and the PASS methodology for writing effective emails. We will also explore the components and importance of communication and e-communication and identify the rules for email.",
      },
      {
        title: "Framing Your Message",
        body: "In this session, we will explore how to eliminate wordy expressions, increase the readability of the written message, define writer's block, and understand the editing process.",
      },
    ],
  },
  "stakeholder-management": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Stakeholder-Management.jpg",
    whoFor:
      "This course is ideal for anyone who manages a project in an organization who would like to develop an effective rapport with involved stakeholders, gaining their trust and approval when necessary.",
    overview:
      "Stakeholder management is the process of maintaining good relationships with the people who have the most impact on your work. To have effective stakeholder management, we need to create long-term relationships based on trust, integrity, and customer satisfaction with the key players involved in the business at any level. Depending on the way those relationships are managed, the business can experience exponential growth…or its growth could stall or even decline. This is why keeping stakeholders satisfied and meeting business expectations is vital for the success of the business. In this course, we will explore the stakeholder management process in detail, including the impact of internal and external stakeholders. We will examine the stakeholder analysis matrix, review the significance of an effective communication strategy, and understand the essential nature of planning that enables us to manage risk and shape decisions.",
    outcomes: [
      "Define stakeholder analysis and recognize its importance within stakeholder management.",
      "Identify the impact of implementing a communication strategy.",
      "Explore the role of planning in stakeholder management.",
    ],
    behaviors: ["Builds Trust", "Communicates", "Solves Problems"],
    sessions: [
      {
        title: "Stakeholder Analysis",
        body: "In this session, we will explore who our stakeholders are; both internally and externally. We will also define the intentional approach to managing our stakeholders and identify and examine the stakeholder analysis matrix and its importance.",
      },
      {
        title: "Communication Strategy",
        body: "In this session, we will identify the importance of a communication strategy in stakeholder management and determine the significance of defining expectations publicly. We will also identify the decision makers and recognize the value of accountability.",
      },
      {
        title: "The Role of Planning",
        body: "In this session, we will analyze the importance of planning in stakeholder management, examine some approaches to assist in planning, and identify methods to manage risk and shape the data needed for making decisions.",
      },
    ],
  },
  "succession-planning-principles": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Succession-Planning-Principles.jpg",
    whoFor:
      "This course is ideal for executives and advanced leaders who are looking for successors and need a better understanding of succession planning.",
    overview:
      "As dynamics in the workforce continue to change, it is an increasing reality that employees no longer stay with the same organization for decades, but rather move from company to company chasing better opportunities and increased job satisfaction. Therefore, it is important for companies to be aware of the needs of their top-performing individuals and find ways to continue to challenge them. It is also important to establish a succession plan to replace employees if they leave the organization. This course will give you the tools to regain positive momentum after a key employee leaves the organization and show you how to create a pipeline of new talent and potential leadership. As a result, you will be able to guide development activities of key employees and bring selection systems, reward systems, and management development into alignment with the process of leadership renewal. You will also learn how to avoid major transition setbacks.",
    outcomes: [
      "Recognize the three models for succession planning.",
      "Utilize employee development plans (EDPs) as a tool for leadership development.",
      "Identify current and future needs by conducting a gap analysis.",
    ],
    behaviors: ["Builds Trust", "Communicates", "Diagnoses Needs", "Inspires"],
    sessions: [
      {
        title: "Introduction to Succession Planning",
        body: "In this session, we will examine Succession Planning and its importance and benefits. We will also understand the old and new principles of succession planning and explore three models for succession planning that are based on top-performing companies.",
      },
      {
        title: "Managing the Talent Pipeline",
        body: "This session explores the six steps of ideal leadership coaching. We will identify the purpose and structure of an employee development plan and recognize the action learning cycle.",
      },
      {
        title: "Creating an Action Plan for Succession Management",
        body: "In this session, we will define and explore three general categories of competency identification. We will discover how to use the gap analysis tool to chart an employee’s strengths and weaknesses. We will complete a 360-degree assessment of an employee’s individual performance and abilities as well as identify how to pinpoint and develop potential leaders.",
      },
    ],
  },
  "turn-your-ideas-into-results": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2025/04/Turn-Your-Ideas-into-Results.jpg",
    whoFor:
      "This course is ideal for leaders who are responsible for bringing innovative products or solutions, leaders of innovative teams, and for any business professional who wants to be more innovative.",
    overview:
      "Gaining a competitive advantage in today’s business environment demands that organizations know how to be innovative. Creativity and the ability to turn ideas into action are critical for standing above the rest. In this course, we will explore how innovation happens and how it moves your organization towards a more innovative-focused mindset. We will examine innovation and the creative process and then learn how to turn ideas into actions.",
    outcomes: [
      "Define innovation and its impact, or lack thereof, on an organization.",
      "Identify the characteristics of creative people and the stages of the creative process.",
      "Explore different practices for implementing innovative ideas.",
    ],
    behaviors: ["Champions Change", "Innovates", "Solves Problems"],
    sessions: [
      {
        title: "Introduction to Innovation",
        body: "In this module, we will define innovation and its impact on an organization. We will differentiate between organizations that have great innovation and those which lack it. We will also explore some of the myths and misconceptions about innovation and identify the multiple intelligence model and its importance.",
      },
      {
        title: "The Creative Process",
        body: "In this module, we will explore the characteristics of creative people and define the stages of the creative process, followed by an overview of innovation implementation.",
      },
      {
        title: "Innovative Ideas Into Action",
        body: "Sometimes we struggle to figure out how to turn innovation through creative ideas into action. In this module, we will examine the implementation practices of innovation and why they are the most important part of the process. We will also identify the ACTION concept that helps us to put things into action effectively.",
      },
    ],
  },
  "uncovering-bias": {
    detailed: true,
    level: "Foundational",
    duration: "3 Weeks",
    weeklyTime: "30 Minutes/Week",
    image:
      "https://leadershipedge.live/wp-content/uploads/2020/10/Uncovering-Bias-Cover-Image-1.png",
    whoFor:
      "This course is ideal for team leaders who are involved in key organizational processes such as recruitment, performance management, promotion, and team dynamics.",
    overview:
      "Bias is something that we all have within us. Biases shape the way we relate to our friends, customers and people around us. Not being aware of our biases can be counterproductive and could create an unhealthy work environment. This course will help build a foundation to recognize and acknowledge individual biases and minimize their impact. We will explore the two most common types of bias and identify skills to address and overcome personal bias.",
    outcomes: [
      "Define bias and its impact.",
      "Differentiate between implicit and explicit biases.",
      "Apply action steps that help mitigate your bias.",
    ],
    behaviors: [
      "Builds Trust",
      "Communicates",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Solves Problems",
    ],
    sessions: [
      {
        title: "Understanding Bias",
        body: "In this module, we will define bias and its impact within the organization and relationships. Participants will learn how to recognize their biases and identify different characteristics, situations and groups that are subject to biases.",
      },
      {
        title: "Two Types of Bias",
        body: "In this module, we will explore the difference between implicit (conscious) bias and explicit (unconscious) bias. We will learn how we use bias to classify people and situations by making several automatic associations. We will explore why some people or situations give you an uneasy feeling and you feel uncomfortable with them.",
      },
      {
        title: "What You Do about Your Bias",
        body: "In this module, we will identify the action steps associated with our biases. We will explore the elements needed to address personal bias including being self-aware, getting into a dialogue, and striving for inclusion.",
      },
    ],
  },
  "weathering-the-storm-learning-to-be-resilient": {
    detailed: true,
    level: "Foundational",
    duration: "4 Weeks",
    weeklyTime: "30 Minutes/Week",
    image: "https://leadershipedge.live/wp-content/uploads/2025/04/Weathering-the-Storm-1.jpg",
    whoFor:
      "This course is designed for any person who is interested in learning how to develop the skills associated with resilience for personal and professional growth.",
    overview:
      "In this course, participants will learn the four fundamental elements that help build resiliency: Attention, Thoughts, Actions, and Motivation. This course will help you apply those skills when faced with a crisis and develop ways to utilize them to continue building resilience in the workplace.",
    outcomes: [
      "Understand how traits, states and the first two key elements influence a person’s ability to be resilient.",
      "Define two additional elements of resilience and identify coping styles that help increase resilience.",
      "Recognize a crisis response cycle and develop ways to interrupt it using coping strategies.",
      "Examine the benefits of building resilient behaviors in the workplace.",
    ],
    behaviors: [
      "Builds Trust",
      "Champions Change",
      "Demonstrates Self-Management",
      "Inspires",
      "Practices Self-Awareness",
      "Pursues Self-Development",
      "Takes Initiative",
    ],
    sessions: [
      {
        title: "Weathering the Storm: Learning Resilience, Part I",
        body: "In this module, we will identify two levels of resiliency, traits and states. Participants will also learn about two key factors for resilience: Attention and Thinking.",
      },
      {
        title: "Weathering the Storm: Learning Resilience, Part II",
        body: "In this module, participants will learn about the next two key factors for resilience: Actions and Motivation. The module will also explore how our sense of control impacts our ability to use positive coping skills (Actions).",
      },
      {
        title: "Weathering the Storm: Resilience in a Time of Crisis",
        body: "In this module, participants will discern how to apply the key elements of resilience during a crisis and discuss five coping skills that can strengthen positive action.",
      },
      {
        title: "Weathering the Storm: Resilience in the Workplace",
        body: "This module looks specifically at how building resiliency in the workplace helps to relieve stress and anxiety and provides a more positive work experience. Specific tips will be discussed to encourage and strengthen resiliency skills.",
      },
    ],
  },
};

const baseCourses: Course[] = [
  {
    slug: "4p-series-communication-from-the-passion-perspective",
    title: "4P Series: Communication from the Passion Perspective",
    courseId: 150,
  },
  {
    slug: "4p-series-communication-from-the-people-perspective",
    title: "4P Series: Communication from the People Perspective",
    courseId: 151,
  },
  {
    slug: "4p-series-communication-from-the-power-perspective",
    title: "4P Series: Communication from the Power Perspective",
    courseId: 146,
  },
  {
    slug: "4p-series-communication-from-the-precise-perspective",
    title: "4P Series: Communication from the Precise Perspective",
    courseId: 152,
  },
  {
    slug: "adapting-your-leadership-style",
    title: "Adapting Your Leadership Style",
    courseId: 139,
  },
  { slug: "analytical-thinking-skills", title: "Analytical Thinking Skills", courseId: 162 },
  { slug: "assertiveness-skills", title: "Assertiveness Skills", courseId: 161 },
  { slug: "balancing-priorities", title: "Balancing Priorities", courseId: 169 },
  {
    slug: "building-authentic-relationships",
    title: "Building Authentic Relationships",
    courseId: 143,
  },
  { slug: "building-employee-engagement", title: "Building Employee Engagement", courseId: 160 },
  { slug: "business-etiquette", title: "Business Etiquette", courseId: 274 },
  {
    slug: "collaborative-coaching-conversations",
    title: "Collaborative Coaching Conversations",
    courseId: 94,
  },
  {
    slug: "improve-communication-through-the-4p-personality-styles",
    title: "Improve Communication Through the 4P Personality Styles",
    courseId: 89,
  },
  {
    slug: "competency-and-behavioral-based-interviewing",
    title: "Competency and Behavioral-Based Interviewing",
    courseId: 179,
  },
  { slug: "competitive-analysis", title: "Competitive Analysis", courseId: 249 },
  { slug: "connecting-across-cultures", title: "Connecting Across Cultures", courseId: 159 },
  { slug: "conquering-conflict", title: "Conquering Conflict", courseId: 153 },
  { slug: "creating-a-culture-of-trust", title: "Creating a Culture of Trust", courseId: 282 },
  { slug: "creative-problem-solving", title: "Creative Problem Solving", courseId: 154 },
  { slug: "customer-service-excellence", title: "Customer Service Excellence", courseId: 213 },
  { slug: "delegating-for-development", title: "Delegating for Development", courseId: 223 },
  { slug: "developing-a-growth-mindset", title: "Developing a Growth Mindset", courseId: 214 },
  { slug: "developing-dynamic-teams", title: "Developing Dynamic Teams", courseId: 261 },
  {
    slug: "developing-others-your-top-priority",
    title: "Developing Others: Your Top Priority",
    courseId: 127,
  },
  {
    slug: "developing-positive-relationships-at-work",
    title: "Developing Positive Relationships at Work",
    courseId: 246,
  },
  {
    slug: "development-to-delegation-cycle",
    title: "Development to Delegation Cycle",
    courseId: 157,
  },
  {
    slug: "driving-results-through-goal-setting",
    title: "Driving Results Through Goal Setting",
    courseId: 148,
  },
  {
    slug: "emotional-and-cognitive-empathy",
    title: "Emotional and Cognitive Empathy",
    courseId: 166,
  },
  { slug: "emotional-triggers", title: "Emotional Triggers", courseId: 219 },
  {
    slug: "equity-diversity-and-inclusion-in-the-workplace",
    title: "Equity, Diversity, and Inclusion in the Workplace",
    courseId: 155,
  },
  { slug: "ethics-in-the-workplace", title: "Ethics in the Workplace", courseId: 241 },
  { slug: "financial-intelligence", title: "Financial Intelligence", courseId: 215 },
  { slug: "finding-your-purpose", title: "Finding Your Purpose", courseId: 235 },
  {
    slug: "fundamentals-of-strategic-planning",
    title: "Fundamentals of Strategic Planning",
    courseId: 243,
  },
  {
    slug: "how-to-make-yourself-indispensable",
    title: "How to Make Yourself Indispensable",
    courseId: 240,
  },
  { slug: "influence-without-authority", title: "Influence Without Authority", courseId: 149 },
  {
    slug: "introduction-to-servant-leadership",
    title: "Introduction to Servant Leadership",
    courseId: 102,
  },
  {
    slug: "leadership-essentials-with-a-foundation-of-emotional-intelligence",
    title: "Leadership Essentials with a Foundation of Emotional Intelligence",
    courseId: 95,
  },
  { slug: "leading-change", title: "Leading Change", courseId: 234 },
  { slug: "leading-leaders", title: "Leading Leaders", courseId: 264 },
  { slug: "leading-remote-teams", title: "Leading Remote Teams", courseId: 97 },
  {
    slug: "leading-with-an-inclusive-mindset",
    title: "Leading with an Inclusive Mindset",
    courseId: 216,
  },
  {
    slug: "making-better-decisions-through-critical-thinking",
    title: "Making Better Decisions Through Critical Thinking",
    courseId: 96,
  },
  {
    slug: "manage-your-time-master-your-life",
    title: "Manage Your Time, Master Your Life",
    courseId: 100,
  },
  { slug: "managing-emotions", title: "Managing Emotions", courseId: 248 },
  {
    slug: "managing-organizational-conflict",
    title: "Managing Organizational Conflict",
    courseId: 245,
  },
  { slug: "managing-stress", title: "Managing Stress", courseId: 210 },
  { slug: "managing-up", title: "Managing Up", courseId: 244 },
  { slug: "masterfully-managing-meetings", title: "Masterfully Managing Meetings", courseId: 254 },
  { slug: "maximizing-strengths", title: "Maximizing Strengths", courseId: 212 },
  {
    slug: "mindfulness-as-a-differentiator",
    title: "Mindfulness as a Differentiator",
    courseId: 260,
  },
  {
    slug: "motivation-models-carrot-or-stick",
    title: "Motivation Models: Carrot or Stick",
    courseId: 167,
  },
  {
    slug: "navigating-organizational-politics",
    title: "Navigating Organizational Politics",
    courseId: 190,
  },
  {
    slug: "negotiation-for-mutual-success",
    title: "Negotiation for Mutual Success",
    courseId: 259,
  },
  { slug: "new-leader-boot-camp", title: "New Leader Boot Camp", courseId: 111 },
  {
    slug: "on-the-spot-development-conversations",
    title: "Giving Feedback Through On-the-Spot Development Conversations",
    courseId: 128,
  },
  { slug: "performance-management", title: "Performance Management", courseId: 273 },
  { slug: "powerful-presentations", title: "Powerful Presentations", courseId: 233 },
  { slug: "practical-project-management", title: "Practical Project Management", courseId: 141 },
  {
    slug: "professional-pathways-building-a-vision-for-your-career",
    title: "Professional Pathways - Building a Vision for Your Career",
    courseId: 203,
  },
  { slug: "psychological-safety", title: "Psychological Safety", courseId: 199 },
  {
    slug: "servant-leadership-ii-manager-as-servant-leader",
    title: "Servant Leadership II: The Manager as the Servant Leader",
    courseId: 251,
  },
  { slug: "skillful-collaboration", title: "Skillful Collaboration", courseId: 256 },
  { slug: "soar-analysis", title: "SOAR Analysis", courseId: 218 },
  { slug: "solid-business-writing", title: "Solid Business Writing", courseId: 283 },
  { slug: "stakeholder-management", title: "Stakeholder Management", courseId: 266 },
  {
    slug: "strengthening-teams-through-communication",
    title: "Strengthening Teams Through Communication",
    courseId: 250,
  },
  {
    slug: "succession-planning-principles",
    title: "Succession Planning Principles",
    courseId: 257,
  },
  { slug: "the-art-of-influence", title: "The Art of Influence", courseId: 265 },
  {
    slug: "the-art-of-small-talk-and-networking",
    title: "The Art of Small Talk and Networking",
    courseId: 186,
  },
  {
    slug: "the-experienced-leader-boot-camp",
    title: "Today’s Leadership Challenges - The Experienced Leader Boot Camp",
    courseId: 208,
  },
  { slug: "the-invincible-influencer", title: "The Invincible Influencer", courseId: 278 },
  { slug: "the-mentoring-mindset", title: "The Mentoring Mindset", courseId: 207 },
  { slug: "true-empowerment", title: "True Empowerment", courseId: 168 },
  { slug: "turn-your-ideas-into-results", title: "Turn Your Ideas into Results", courseId: 205 },
  { slug: "uncovering-bias", title: "Uncovering Bias", courseId: 184 },
  {
    slug: "weathering-the-storm-learning-to-be-resilient",
    title: "Weathering the Storm: Learning to Be Resilient",
    courseId: 101,
  },
];

// Course images live in src/assets/courses/<slug>.webp and override any remote URL.
const courseImages: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("../assets/courses/*.webp", {
      eager: true,
      import: "default",
    }),
  ).map(([path, url]) => [path.split("/").pop()!.replace(/\.webp$/, ""), url]),
);

export const courses: Course[] = baseCourses.map((c) => {
  const merged: Course = {
    ...c,
    ...(FEATURED_SLUGS.has(c.slug) ? { featured: true } : {}),
    ...(details[c.slug] ?? {}),
  };
  const local = courseImages[c.slug];
  return local ? { ...merged, image: local } : merged;
});

/** Courses left out of the /course-catalog list (their own pages still work). */
const HIDDEN_FROM_CATALOG = new Set(["solid-business-writing"]);
export const catalogCourses: Course[] = courses.filter((c) => !HIDDEN_FROM_CATALOG.has(c.slug));

export function getCourse(slug: string): Course | undefined {
  return courses.find((c) => c.slug === slug);
}

export const featuredCourses: Course[] = courses.filter((c) => c.featured);

/** Extra picks featured only within a Leader Behavior filter, so every behavior has 3+. */
const BEHAVIOR_FEATURED_EXTRAS: Record<string, string[]> = {
  Innovates: ["turn-your-ideas-into-results", "soar-analysis"],
  "Leverages Diversity": [
    "leading-with-an-inclusive-mindset",
    "connecting-across-cultures",
    "equity-diversity-and-inclusion-in-the-workplace",
  ],
};

/** Featured courses for one Leader Behavior: sitewide featured matches plus behavior extras. */
export function featuredSlugsForBehavior(behavior: string): Set<string> {
  const extras = BEHAVIOR_FEATURED_EXTRAS[behavior] ?? [];
  return new Set(
    courses
      .filter(
        (c) =>
          (c.behaviors ?? []).includes(behavior) && (c.featured || extras.includes(c.slug)),
      )
      .map((c) => c.slug),
  );
}

export const courseLevels: string[] = Array.from(
  new Set(courses.map((c) => c.level).filter((l): l is string => Boolean(l))),
).sort();

export const courseBehaviorNames: string[] = Array.from(
  new Set(courses.flatMap((c) => c.behaviors ?? [])),
).sort();
